import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, HostListener, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { FikoThreeJsComponent } from '../../../fiko-three-js.component';
import { FikoOlcumCizgisi } from '../../FikoOlcumCizgisi';

/**
 * 🎯 CATIA V5 Tarzı Ölçüm Düzenleme Dialog'u
 *
 * ÖZELLİKLER:
 * - Ölçüm değeri, birim ve ondalık basamak düzenlenebilir
 * - Çizgi ve metin rengi ayrı ayrı değiştirilebilir
 * - Metin boyutu ve çizgi kalınlığı ayarlanabilir
 * - Parça görünürlükleri (çizgi, ok, metin) ayrı ayrı kontrol edilebilir
 * - Ok stili seçilebilir (ok, nokta, kare, yok)
 * - Metin arkaplan rengi ve saydamlığı ayarlanabilir
 * - Dialog açıkken 3D sahnede sürükleme/döndürme çalışır
 * - Sabit boyutlu label (zoom'dan etkilenmez)
 * - Pozisyon bilgileri gösterilir
 * - Varsayılana sıfırla özelliği
 */
@Component({
  selector: 'fikoyt-olcum-duzenle-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './olcum-duzenle-dialog.component.html',
  styleUrls: ['./olcum-duzenle-dialog.component.scss']
})
export class OlcumDuzenleDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() olcumGrubu: THREE.Group;
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;
  @Output() kapandi = new EventEmitter<void>();
  @Output() guncellendi = new EventEmitter<void>();

  // === SEKMELER ===
  aktifSekme: 'deger' | 'gorunum' | 'gelismis' = 'deger';

  // === TEMEL BİLGİLER ===
  isim: string = '';
  tarih: string = '';
  gorunur: boolean = true;

  // === ÖLÇÜM DEĞERİ ===
  olcumDegeri: number = 0;
  birim: string = 'mm';
  birimler = ['mm', 'cm', 'm', 'inch', 'ft'];
  ondalikBasamak: number = 2;

  // 🎯 v3.3.0: Koordinat ölçümü desteği
  olcumTuru: string = 'mesafe';  // 'mesafe' | 'koordinat' | 'cap' | 'yaricap' | 'uzunluk' | 'alan' | 'aci'
  koordinatNoktasi: { x: number; y: number; z: number } | null = null;

  // === RENK AYARLARI === 🎯 v3.2.0: Siyah çizgi/ok, beyaz yazı, siyah arkaplan
  cizgiRengi: string = '#000000';
  okRengi: string = '#000000';
  metinRengi: string = '#ffffff';
  metinArkaplanRengi: string = '#000000';
  metinArkaplanSaydamlik: number = 100;

  // === BOYUT AYARLARI ===
  metinBoyutu: number = 14;
  cizgiKalinligi: number = 2;
  okBoyutu: number = 8;

  // === OK STİLİ ===
  okStili: 'ok' | 'nokta' | 'kare' | 'yok' = 'ok';
  okStilleri = [
    { value: 'ok', label: 'Ok ▶' },
    { value: 'nokta', label: 'Nokta ●' },
    { value: 'kare', label: 'Kare ■' },
    { value: 'yok', label: 'Yok' }
  ];

  // === GÖRÜNÜRLÜK ===
  cizgiGorunur: boolean = true;
  okGorunur: boolean = true;
  metinGorunur: boolean = true;
  uzatmaCizgisiGorunur: boolean = true;

  // === POZİSYON BİLGİLERİ ===
  pozisyon1 = { x: 0, y: 0, z: 0 };
  pozisyon2 = { x: 0, y: 0, z: 0 };

  // === ÖZEL EFEKTLER ===
  glowEfekti: boolean = false;
  glowYogunluk: number = 5;
  golgeEfekti: boolean = true;

  // === SABİT BOYUT ===
  sabitBoyut: boolean = true;  // 🎯 v3.2.3: Zoom'dan bağımsız yazı boyutu

  // === TAŞIMA KONTROLÜ ===
  tasimaModu: boolean = false;

  // === PRİVATE ===
  private orijinalDegerler: any = {};
  private orijinalSpriteScale: THREE.Vector3 | null = null;
  private kontrollerOncekiDurum: boolean = true;
  private yuklendi: boolean = false;
  private orijinalOffset: number = 15;  // FikoOlcumCizgisi için orijinal offset
  private canvasElement: HTMLCanvasElement | null = null;

  // ====================================================================
  // 🎯 DRAG SYSTEM - DraggableSupport.ts'den BİREBİR KOPYALANDI
  // ====================================================================

  // Drag mode enum (DraggableSupport satır 28-31)
  private readonly DRAG_MODE_OFFSET = 'OFFSET';    // Normal sürükleme (offset değişir)
  private readonly DRAG_MODE_ROTATE = 'ROTATE';    // Döndürme (Shift tuşu ile)

  // Drag state (DraggableSupport satır 38-51)
  private dragState = {
    active: false,
    startMouse: new THREE.Vector2(),
    plane: new THREE.Plane(),
    startDimensionOffset: 0,
    offsetDirection: new THREE.Vector3(),
    mode: 'OFFSET' as string,
    startRotation: 0
  };

  // Orijinal değerler (sıfırlama için)
  private orijinalPozisyon: THREE.Vector3 = new THREE.Vector3();
  private orijinalRotasyon: number = 0;

  // 🎯 v3.0.0: Plain Group için LinearDimension tarzı offset/rotation
  // pointA ve pointB SABİT KALIR, sadece dimension görüntüsü değişir
  private plainGroupPointA: THREE.Vector3 = new THREE.Vector3();
  private plainGroupPointB: THREE.Vector3 = new THREE.Vector3();
  private plainGroupOffset: number = 15;  // Mevcut offset (geometriden dimension line'a mesafe)
  private plainGroupRotation: number = 0; // Döndürme açısı (radyan)
  private plainGroupOrijinalOffset: number = 15;  // Sıfırlama için orijinal offset
  private plainGroupOrijinalRotation: number = 0; // Sıfırlama için orijinal rotation

  // 🎯 v3.2.0: Sürükleme için düzlem ve kesişim noktası
  private dragPlane: THREE.Plane = new THREE.Plane();
  private dragIntersection: THREE.Vector3 = new THREE.Vector3();

  // 🚀 PERFORMANS: Pre-allocated buffers (zero-allocation) (DraggableSupport satır 82-87)
  private static readonly _tempVec2 = new THREE.Vector2();
  private static readonly _tempVec3 = new THREE.Vector3();
  private static readonly _tempVec3_2 = new THREE.Vector3();
  private static readonly _tempRay = new THREE.Ray();
  private static readonly _raycaster = new THREE.Raycaster();

  // 🚀 PERFORMANS: Throttle settings (DraggableSupport satır 89-95)
  private lastMoveTime: number = 0;
  private readonly MOVE_THROTTLE_MS = 16; // ~60fps
  private rafId: number | null = null;
  private pendingMouse: THREE.Vector2 | null = null;
  private pendingShiftKey: boolean = false;

  // Bound event handlers (GC optimization) (DraggableSupport satır 97-101)
  private boundOnPointerDown: ((e: PointerEvent) => void) | null = null;
  private boundOnPointerMove: ((e: PointerEvent) => void) | null = null;
  private boundOnPointerUp: ((e: PointerEvent) => void) | null = null;
  private boundOnPointerCancel: ((e: PointerEvent) => void) | null = null;
  // 🎯 v3.2.12: Shift tuşu bırakıldığında mod değişikliği için
  private boundOnKeyUp: ((e: KeyboardEvent) => void) | null = null;
  private boundOnKeyDown: ((e: KeyboardEvent) => void) | null = null;
  // 🎯 v3.2.12: Son bilinen mouse pozisyonu (keyup event'i için)
  private lastMousePosition: THREE.Vector2 = new THREE.Vector2();

  constructor(private CD: ChangeDetectorRef) {}

  ngOnInit(): void {
    // console.log('[OlcumDuzenleDialog] ngOnInit - olcumGrubu:', this.olcumGrubu);
    // console.log('[OlcumDuzenleDialog] ngOnInit - anaGosterim:', !!this.anaGosterim);

    this.degerlerYukle();
    this.orijinalDegerleriKaydet();
    this.sahneKontrolleriniAyarla();
    this.yuklendi = true;
  }

  ngAfterViewInit(): void {
    // View init olduktan sonra kontrolleri tekrar aktif et
    // ve dimmer pointer-events sorununu düzelt
    setTimeout(() => {
      this.sahneKontrolleriniAyarla();
      this.dimmerPointerEventsDuzelt();
      this.CD.detectChanges();
    }, 100);

    // İkinci bir kontrol daha yap (modal animation sonrası)
    setTimeout(() => {
      this.sahneKontrolleriniAyarla();
      this.dimmerPointerEventsDuzelt();
    }, 300);
  }

  /**
   * 🎯 KRİTİK: Dimmer'ın pointer-events'ini düzelt
   * Semantic UI modal dimmer 3D canvas'ı engelliyor
   */
  private dimmerPointerEventsDuzelt(): void {
    // Tüm dimmer ve modal container'ları kontrol et
    const dimmerElements = document.querySelectorAll('.ui.dimmer, .ui.modals, .modals.dimmer');
    dimmerElements.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.pointerEvents = 'none';
    });

    // Modal'ın kendisi pointer-events: all olmalı
    const modalElements = document.querySelectorAll('.ui.modal');
    modalElements.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.pointerEvents = 'all';
    });

    // console.log('[OlcumDuzenleDialog] Dimmer pointer-events düzeltildi');
  }

  ngOnDestroy(): void {
    // Taşıma modunu temizle (event listener'lar)
    this.tasimaModuKapat();
    this.sahneKontrolleriniGeriYukle();
  }

  /**
   * Dialog üzerinde mousedown - event'i durdurma, sadece kontrolleri devre dışı bırak
   */
  onDialogMouseDown(event: MouseEvent): void {
    // Dialog içinde tıklandığında OrbitControls'u geçici olarak devre dışı bırakma
    // Böylece input'lara yazarken sahne dönmez
    // AMA: Bu metodu çağırmıyoruz artık, kontroller her zaman aktif
  }

  /**
   * Dialog dışında tıklama - 3D sahnede çalışmasını sağla
   */
  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Her durumda OrbitControls aktif olsun
    this.kontrollerAktifEt();
  }

  // ==================== 3D SAHNE KONTROL ====================

  /**
   * Dialog açıkken OrbitControls HER ZAMAN aktif
   */
  private sahneKontrolleriniAyarla(): void {
    if (!this.anaGosterim?.aktifKontrol) {
      console.warn('[OlcumDuzenleDialog] aktifKontrol bulunamadı');
      return;
    }

    // Mevcut durumu kaydet
    this.kontrollerOncekiDurum = this.anaGosterim.aktifKontrol.enabled;

    // 🎯 KRİTİK: OrbitControls'u HER ZAMAN aktif tut
    this.anaGosterim.aktifKontrol.enabled = true;

    // Canvas'a erişim sağla ve pointer-events'i kontrol et
    const canvas = this.anaGosterim.aktifKontrol.domElement;
    if (canvas) {
      canvas.style.pointerEvents = 'auto';
    }

    // console.log('[OlcumDuzenleDialog] OrbitControls aktif, enabled:', this.anaGosterim.aktifKontrol.enabled);
  }

  /**
   * OrbitControls'u aktif et
   */
  private kontrollerAktifEt(): void {
    if (this.anaGosterim?.aktifKontrol) {
      this.anaGosterim.aktifKontrol.enabled = true;
    }
  }

  /**
   * Dialog kapanınca eski duruma geri dön
   */
  private sahneKontrolleriniGeriYukle(): void {
    if (this.anaGosterim?.aktifKontrol) {
      this.anaGosterim.aktifKontrol.enabled = this.kontrollerOncekiDurum;
    }
  }

  // ==================== DEĞER YÜKLEME ====================

  private degerlerYukle(): void {
    if (!this.olcumGrubu) {
      console.warn('[OlcumDuzenleDialog] olcumGrubu yok!');
      return;
    }

    // console.log('[OlcumDuzenleDialog] Değerler yükleniyor...', {
    //   name: this.olcumGrubu.name,
    //   userData: JSON.stringify(this.olcumGrubu.userData, null, 2),
    //   childCount: this.olcumGrubu.children.length
    // });

    const userData = this.olcumGrubu.userData || {};

    // 🎯 v3.3.0: Ölçüm türünü ve koordinat noktasını al
    this.olcumTuru = userData.olcumTuru || 'mesafe';
    this.koordinatNoktasi = userData.koordinatNoktasi || null;
    // console.log('[OlcumDuzenleDialog] Ölçüm türü:', this.olcumTuru, 'koordinatNoktasi:', this.koordinatNoktasi);

    // 🎯 v3.3.4 FIX: FikoOlcumCizgisi klonlandığında olcumTuru property'si kopyalanmıyor
    // userData'dan alınan değeri instance'a da set et
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      (this.olcumGrubu as FikoOlcumCizgisi).olcumTuru = this.olcumTuru as any;
      // console.log('[OlcumDuzenleDialog] v3.3.4 FikoOlcumCizgisi.olcumTuru güncellendi:', this.olcumTuru);
    }

    // Temel bilgiler
    this.isim = userData.olcumIsim || userData.olcumBaslik || this.olcumGrubu.name || '';
    this.tarih = this.tarihFormatla(userData.olusturmaTarihi);
    this.gorunur = this.olcumGrubu.visible;

    // 🎯 Pozisyonları ÖNCE al - mesafe hesabı için gerekli
    this.pozisyonlariAl(userData);

    // 🎯 v3.2.0: Plain Group için pointA/B ve offset değerlerini ayarla
    this.plainGroupPointA.set(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
    this.plainGroupPointB.set(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);
    this.plainGroupOffset = userData.offset ?? this.offsetHesapla() ?? 15;
    this.plainGroupRotation = userData.rotation ?? 0;
    this.plainGroupOrijinalOffset = this.plainGroupOffset;
    this.plainGroupOrijinalRotation = this.plainGroupRotation;

    // console.log('[OlcumDuzenleDialog] v3.2.0 Plain Group degerleri:', {
    //   pointA: this.plainGroupPointA.toArray(),
    //   pointB: this.plainGroupPointB.toArray(),
    //   offset: this.plainGroupOffset,
    //   rotation: this.plainGroupRotation
    // });

    // 🎯 v3.2.9: Ölçüm değeri - GERÇEK DEĞER ÖNCELİKLİ
    // Öncelik sırası: gercekDeger > cap/uzunluk/mesafe/alan > value/distance > baslik parse > pozisyon hesapla
    let degerBulundu = false;

    // 🎯 v3.3.0: Koordinat türü için özel işlem - yaziParams'tan metin al
    if (this.olcumTuru === 'koordinat') {
      // Koordinat ölçümünde olcumDegeri kullanılmaz, koordinat noktası kullanılır
      // yaziParams varsa mevcut metni koru
      if (userData.yaziParams?.metin) {
        // console.log('[OlcumDuzenleDialog] Koordinat metni yaziParams\'tan alındı:', userData.yaziParams.metin);
        degerBulundu = true; // Koordinat için değer aramasını atla
      } else if (this.koordinatNoktasi) {
        // console.log('[OlcumDuzenleDialog] Koordinat noktası bulundu:', this.koordinatNoktasi);
        degerBulundu = true; // Koordinat için değer aramasını atla
      }
    }

    // 1. EN ÖNCELİKLİ - gercekDeger (dialog'da düzenleme yapıldıktan sonra kaydedilen)
    if (!degerBulundu && userData.gercekDeger !== undefined && userData.gercekDeger !== null && !isNaN(userData.gercekDeger)) {
      this.olcumDegeri = userData.gercekDeger;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 1. gercekDeger\'den değer alındı (tam hassasiyet):', this.olcumDegeri);
    }

    // 2. Gerçek değer alanları (kalıcı ölçüm yapılırken kaydedilen)
    if (!degerBulundu && userData.cap !== undefined && userData.cap !== null && userData.cap !== 0) {
      this.olcumDegeri = userData.cap;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. cap\'ten değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.yaricap !== undefined && userData.yaricap !== null && userData.yaricap !== 0) {
      this.olcumDegeri = userData.yaricap;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. yaricap\'ten değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.uzunluk !== undefined && userData.uzunluk !== null && userData.uzunluk !== 0) {
      this.olcumDegeri = userData.uzunluk;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. uzunluk\'tan değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.mesafe !== undefined && userData.mesafe !== null && userData.mesafe !== 0) {
      this.olcumDegeri = userData.mesafe;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. mesafe\'den değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.alan !== undefined && userData.alan !== null && userData.alan !== 0) {
      this.olcumDegeri = userData.alan;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. alan\'dan değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.diameter !== undefined && userData.diameter !== null && userData.diameter !== 0) {
      this.olcumDegeri = userData.diameter;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. diameter\'den değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.radius !== undefined && userData.radius !== null && userData.radius !== 0) {
      this.olcumDegeri = userData.radius;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 2. radius\'ten değer alındı:', this.olcumDegeri);
    }

    // 3. value/distance alanları
    if (!degerBulundu && userData.value !== undefined && userData.value !== null && userData.value !== 0) {
      this.olcumDegeri = userData.value;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 3. value\'den değer alındı:', this.olcumDegeri);
    } else if (!degerBulundu && userData.distance !== undefined && userData.distance !== null && userData.distance !== 0) {
      this.olcumDegeri = userData.distance;
      degerBulundu = true;
      // console.log('[OlcumDuzenleDialog] 3. distance\'ten değer alındı:', this.olcumDegeri);
    }

    // 4. Başlıktan parse et (yuvarlanmış olabilir - son çare olarak kullan)
    if (userData.FikoArayuz?.baslik && !degerBulundu) {
      const parsedValue = this.baslikDegerParseEt(userData.FikoArayuz.baslik);
      if (parsedValue !== null) {
        this.olcumDegeri = parsedValue.deger;
        this.birim = parsedValue.birim || 'mm';
        degerBulundu = true;
        // console.log('[OlcumDuzenleDialog] 4. FikoArayuz.baslik\'ten değer alındı (yuvarlanmış olabilir):', this.olcumDegeri, this.birim);
      }
    }

    // 5. olcumBaslik'ten parse et
    if (userData.olcumBaslik && !degerBulundu) {
      const parsedValue = this.baslikDegerParseEt(userData.olcumBaslik);
      if (parsedValue !== null) {
        this.olcumDegeri = parsedValue.deger;
        this.birim = parsedValue.birim || 'mm';
        degerBulundu = true;
        // console.log('[OlcumDuzenleDialog] 5. olcumBaslik\'ten değer alındı (yuvarlanmış olabilir):', this.olcumDegeri, this.birim);
      }
    }

    // 6. Pozisyonlardan mesafe hesapla (son çare)
    if (!degerBulundu) {
      const hesaplananMesafe = this.mesafeHesapla();
      if (hesaplananMesafe > 0.001) {
        this.olcumDegeri = hesaplananMesafe;
        degerBulundu = true;
        // console.log('[OlcumDuzenleDialog] Mesafe hesaplandı:', this.olcumDegeri);
      }
    }

    // Birim - userData'dan al (eğer yukarıda ayarlanmadıysa)
    if (!this.birim || this.birim === 'mm') {
      this.birim = userData.unit || userData.birim || 'mm';
    }
    this.ondalikBasamak = userData.ondalikBasamak ?? 2;

    // 🎯 Renkler - ÖNCE 3D objelerden oku, userData'da yoksa
    const cizgiRenkGercek = this.cizgiRenginiAl();
    const okRenkGercek = this.okRenginiAl();
    const metinRenkGercek = this.metinRenginiAl();

    this.cizgiRengi = userData.cizgiRengi || cizgiRenkGercek || '#000000';
    this.okRengi = userData.okRengi || okRenkGercek || this.cizgiRengi;
    this.metinRengi = userData.textColor || userData.metinRengi || metinRenkGercek || '#ffffff';

    // 🎯 v3.2.0: Sprite'dan tüm özellikleri al
    const spriteOzellikleri = this.spriteOzellikleriniAl();
    this.metinArkaplanRengi = userData.metinArkaplanRengi || spriteOzellikleri.metinArkaplanRengi || '#000000';
    this.metinArkaplanSaydamlik = userData.metinArkaplanSaydamlik ?? spriteOzellikleri.metinArkaplanSaydamlik ?? 70;

    // 🎯 Boyutlar - ÖNCE 3D objelerden oku
    const cizgiKalinlikGercek = this.cizgiKalinliginiAl();
    const metinBoyutuGercek = this.metinBoyutunuAl();
    const okBoyutuGercek = this.okBoyutunuAl();
    const okStiliGercek = this.okStiliniAl();

    this.metinBoyutu = userData.fontSize ?? metinBoyutuGercek ?? 14;
    this.cizgiKalinligi = userData.lineWidth ?? cizgiKalinlikGercek ?? 2;
    this.okBoyutu = userData.okBoyutu ?? okBoyutuGercek ?? 8;

    // Ok stili - 3D objelerden veya userData'dan
    this.okStili = userData.okStili || okStiliGercek || 'ok';

    // 🎯 Görünürlükler - 3D objelerden oku
    this.gorunurlukDurumlariniAl();

    // 🎯 v3.2.0: Efektler - sprite'dan veya userData'dan
    this.glowEfekti = userData.glowEfekti ?? spriteOzellikleri.glowEfekti ?? false;
    this.glowYogunluk = userData.glowYogunluk ?? spriteOzellikleri.glowYogunluk ?? 5;
    this.golgeEfekti = userData.golgeEfekti ?? spriteOzellikleri.golgeEfekti ?? true;

    // 🎯 v3.2.3: Sabit boyut - olcumGrubu'ndan veya userData'dan oku
    if ((this.olcumGrubu as any).sabitBoyut !== undefined) {
      this.sabitBoyut = (this.olcumGrubu as any).sabitBoyut;
    } else {
      this.sabitBoyut = userData.sabitBoyut ?? true;
    }

    // Sprite scale kaydet
    this.orijinalSpriteScaleKaydet();

    // console.log('[OlcumDuzenleDialog] v3.2.0 Yuklenen tum degerler:', {
    //   // Temel
    //   olcumDegeri: this.olcumDegeri,
    //   birim: this.birim,
    //   // Renkler
    //   cizgiRengi: this.cizgiRengi,
    //   okRengi: this.okRengi,
    //   metinRengi: this.metinRengi,
    //   metinArkaplanRengi: this.metinArkaplanRengi,
    //   metinArkaplanSaydamlik: this.metinArkaplanSaydamlik,
    //   // Boyutlar
    //   metinBoyutu: this.metinBoyutu,
    //   cizgiKalinligi: this.cizgiKalinligi,
    //   okBoyutu: this.okBoyutu,
    //   okStili: this.okStili,
    //   // Efektler
    //   glowEfekti: this.glowEfekti,
    //   glowYogunluk: this.glowYogunluk,
    //   golgeEfekti: this.golgeEfekti,
    //   // Sabit Boyut
    //   sabitBoyut: this.sabitBoyut,
    //   olcumGrubuSabitBoyut: (this.olcumGrubu as any).sabitBoyut,
    //   // Offset
    //   plainGroupOffset: this.plainGroupOffset,
    //   plainGroupRotation: this.plainGroupRotation
    // });

    this.CD.detectChanges();
  }

  /**
   * 🎯 v3.2.0: 3D çizgilerden renk al - LineSegments2/Mesh+LineGeometry desteği
   */
  private cizgiRenginiAl(): string | null {
    let renk: string | null = null;
    this.olcumGrubu.traverse((child) => {
      if (renk) return;

      // 🎯 LineSegments2 veya Mesh+LineGeometry (LineMaterial)
      if (this.isLineBased(child)) {
        const mat = (child as any).material;
        if (mat?.color) {
          renk = '#' + mat.color.getHexString();
        }
      }
      // Normal THREE.Line
      else if (child instanceof THREE.Line) {
        const mat = child.material as THREE.LineBasicMaterial;
        if (mat?.color) {
          renk = '#' + mat.color.getHexString();
        }
      }
    });
    return renk;
  }

  /**
   * 3D ok mesh'lerinden renk al
   */
  private okRenginiAl(): string | null {
    let renk: string | null = null;
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Mesh && !renk) {
        const mat = child.material as THREE.MeshBasicMaterial;
        if (mat?.color) {
          renk = '#' + mat.color.getHexString();
        }
      }
    });
    return renk;
  }

  /**
   * Sprite'dan metin rengini al (canvas'tan okuyamayız, userData'dan alırız)
   */
  private metinRenginiAl(): string | null {
    let renk: string | null = null;
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite && child.userData?.textColor) {
        renk = child.userData.textColor;
      }
    });
    return renk;
  }

  /**
   * 🎯 v3.2.0: 3D çizgilerden kalınlık al - LineMaterial desteği
   */
  private cizgiKalinliginiAl(): number | null {
    let kalinlik: number | null = null;
    this.olcumGrubu.traverse((child) => {
      if (kalinlik !== null) return;

      // 🎯 LineMaterial (LineSegments2, Mesh+LineGeometry)
      if (this.isLineBased(child)) {
        const mat = (child as any).material;
        if (mat?.linewidth) {
          kalinlik = mat.linewidth;
        }
      }
      // Normal THREE.Line
      else if (child instanceof THREE.Line) {
        const mat = child.material as THREE.LineBasicMaterial;
        if (mat?.linewidth) {
          kalinlik = mat.linewidth;
        }
      }
    });
    return kalinlik;
  }

  /**
   * Sprite'dan font boyutunu al
   */
  private metinBoyutunuAl(): number | null {
    let boyut: number | null = null;
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite && child.userData?.fontSize) {
        boyut = child.userData.fontSize;
      }
    });
    return boyut;
  }

  /**
   * 🎯 v3.2.0: Sprite'dan tüm metin özelliklerini al
   * (arkaplan rengi, saydamlık, glow efekti vb.)
   */
  private spriteOzellikleriniAl(): {
    metinArkaplanRengi: string | null;
    metinArkaplanSaydamlik: number | null;
    glowEfekti: boolean | null;
    glowYogunluk: number | null;
    golgeEfekti: boolean | null;
  } {
    const sonuc = {
      metinArkaplanRengi: null as string | null,
      metinArkaplanSaydamlik: null as number | null,
      glowEfekti: null as boolean | null,
      glowYogunluk: null as number | null,
      golgeEfekti: null as boolean | null
    };

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite) {
        const ud = child.userData || {};

        // Arkaplan rengi
        if (ud.backgroundColor || ud.bgColor || ud.metinArkaplanRengi) {
          sonuc.metinArkaplanRengi = ud.backgroundColor || ud.bgColor || ud.metinArkaplanRengi;
        }

        // Arkaplan saydamlığı (0-100 veya 0-1 olabilir)
        if (ud.backgroundOpacity !== undefined || ud.bgOpacity !== undefined || ud.metinArkaplanSaydamlik !== undefined) {
          let saydamlik = ud.backgroundOpacity ?? ud.bgOpacity ?? ud.metinArkaplanSaydamlik;
          // 0-1 aralığındaysa 0-100'e çevir
          if (saydamlik <= 1) {
            saydamlik = saydamlik * 100;
          }
          sonuc.metinArkaplanSaydamlik = saydamlik;
        }

        // Glow efekti
        if (ud.glow !== undefined || ud.glowEfekti !== undefined) {
          sonuc.glowEfekti = ud.glow ?? ud.glowEfekti;
        }

        // Glow yoğunluk
        if (ud.glowIntensity !== undefined || ud.glowYogunluk !== undefined) {
          sonuc.glowYogunluk = ud.glowIntensity ?? ud.glowYogunluk;
        }

        // Gölge efekti
        if (ud.shadow !== undefined || ud.golgeEfekti !== undefined) {
          sonuc.golgeEfekti = ud.shadow ?? ud.golgeEfekti;
        }
      }
    });

    return sonuc;
  }

  /**
   * 🎯 v3.2.0: Ok mesh'lerinden ok boyutunu al
   */
  private okBoyutunuAl(): number | null {
    let boyut: number | null = null;
    this.olcumGrubu.traverse((child) => {
      if (boyut !== null) return;

      // Ok mesh'i (Sprite değil, Line değil)
      if (child instanceof THREE.Mesh && !(child instanceof THREE.Sprite) && !this.isLineBased(child)) {
        // ConeGeometry'den boyut al
        const geo = child.geometry;
        if (geo instanceof THREE.ConeGeometry) {
          // Cone yüksekliği ok boyutudur
          boyut = (geo.parameters as any)?.height ?? null;
        }
        // userData'dan al
        else if (child.userData?.arrowSize || child.userData?.okBoyutu) {
          boyut = child.userData.arrowSize ?? child.userData.okBoyutu;
        }
        // Scale'den tahmin et
        else if (child.scale) {
          boyut = Math.max(child.scale.x, child.scale.y, child.scale.z) * 8; // Yaklaşık
        }
      }
    });
    return boyut;
  }

  /**
   * 🎯 v3.2.0: Ok stilini tespit et
   */
  private okStiliniAl(): 'ok' | 'nokta' | 'kare' | 'yok' | null {
    let stil: 'ok' | 'nokta' | 'kare' | 'yok' | null = null;
    this.olcumGrubu.traverse((child) => {
      if (stil !== null) return;

      if (child instanceof THREE.Mesh && !(child instanceof THREE.Sprite) && !this.isLineBased(child)) {
        const geo = child.geometry;

        // userData'dan al
        if (child.userData?.arrowStyle || child.userData?.okStili) {
          stil = child.userData.arrowStyle ?? child.userData.okStili;
          return;
        }

        // Geometry tipinden tahmin et
        if (geo instanceof THREE.ConeGeometry) {
          stil = 'ok';
        } else if (geo instanceof THREE.SphereGeometry) {
          stil = 'nokta';
        } else if (geo instanceof THREE.BoxGeometry) {
          stil = 'kare';
        }
      }
    });
    return stil;
  }

  /**
   * 3D objelerden görünürlük durumlarını al
   */
  private gorunurlukDurumlariniAl(): void {
    let cizgiBulundu = false;
    let okBulundu = false;
    let metinBulundu = false;
    let uzatmaBulundu = false;

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line) {
        if (child.name?.includes('uzatma') || child.userData?.isExtension) {
          this.uzatmaCizgisiGorunur = child.visible;
          uzatmaBulundu = true;
        } else if (!cizgiBulundu) {
          this.cizgiGorunur = child.visible;
          cizgiBulundu = true;
        }
      }
      if (child instanceof THREE.Mesh && !okBulundu) {
        this.okGorunur = child.visible;
        okBulundu = true;
      }
      if (child instanceof THREE.Sprite && !metinBulundu) {
        this.metinGorunur = child.visible;
        metinBulundu = true;
      }
    });
  }

  private pozisyonlariAl(userData: any): void {
    // 🎯 v3.3.0: FikoOlcumCizgisi ise doğrudan nokta1/nokta2 kullan
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      const fikoOlcum = this.olcumGrubu as FikoOlcumCizgisi;
      // FikoOlcumCizgisi'nin position'u world koordinatında nokta1'e set edilmiş
      // nokta1 local origin (0,0,0), nokta2 ise relative pozisyon
      const worldPos = fikoOlcum.position.clone();

      // Koordinat ölçümü için koordinatNoktasi kullan
      if (userData.koordinatNoktasi) {
        this.pozisyon1 = {
          x: userData.koordinatNoktasi.x,
          y: userData.koordinatNoktasi.y,
          z: userData.koordinatNoktasi.z
        };
        this.pozisyon2 = { ...this.pozisyon1 };
      } else {
        this.pozisyon1 = { x: worldPos.x, y: worldPos.y, z: worldPos.z };
        // nokta2 local koordinat, world'e çevir
        const nokta2World = fikoOlcum.ikinciNokta.clone().add(worldPos);
        this.pozisyon2 = { x: nokta2World.x, y: nokta2World.y, z: nokta2World.z };
      }

      // console.log('[OlcumDuzenleDialog] FikoOlcumCizgisi pozisyonları:', {
      //   pozisyon1: this.pozisyon1,
      //   pozisyon2: this.pozisyon2
      // });
      return;
    }

    // Önce userData'dan dene
    let p1Bulundu = false;
    let p2Bulundu = false;

    if (userData.startPoint) {
      this.pozisyon1 = {
        x: userData.startPoint.x ?? 0,
        y: userData.startPoint.y ?? 0,
        z: userData.startPoint.z ?? 0
      };
      p1Bulundu = true;
    }
    if (userData.endPoint) {
      this.pozisyon2 = {
        x: userData.endPoint.x ?? 0,
        y: userData.endPoint.y ?? 0,
        z: userData.endPoint.z ?? 0
      };
      p2Bulundu = true;
    }

    // p1 veya p2 bulunamadıysa point1/point2 dene
    if (!p1Bulundu && userData.point1) {
      this.pozisyon1 = {
        x: userData.point1.x ?? 0,
        y: userData.point1.y ?? 0,
        z: userData.point1.z ?? 0
      };
      p1Bulundu = true;
    }
    if (!p2Bulundu && userData.point2) {
      this.pozisyon2 = {
        x: userData.point2.x ?? 0,
        y: userData.point2.y ?? 0,
        z: userData.point2.z ?? 0
      };
      p2Bulundu = true;
    }

    // Hala bulunamadıysa children'dan bul
    if (!p1Bulundu || !p2Bulundu) {
      this.pozisyonlariChildrendenAl();
    }

    // console.log('[OlcumDuzenleDialog] Pozisyonlar:', {
    //   pozisyon1: this.pozisyon1,
    //   pozisyon2: this.pozisyon2,
    //   p1Bulundu,
    //   p2Bulundu
    // });
  }

  private pozisyonlariChildrendenAl(): void {
    const positions: THREE.Vector3[] = [];

    // World matrix'leri güncelle
    this.olcumGrubu.updateMatrixWorld(true);

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line && positions.length < 4) {
        const geo = child.geometry as THREE.BufferGeometry;
        const posAttr = geo?.getAttribute('position');
        if (posAttr && posAttr.count >= 2) {
          child.updateMatrixWorld(true);
          const worldMatrix = child.matrixWorld;

          const p1 = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
          const p2 = new THREE.Vector3(posAttr.getX(posAttr.count - 1), posAttr.getY(posAttr.count - 1), posAttr.getZ(posAttr.count - 1));
          p1.applyMatrix4(worldMatrix);
          p2.applyMatrix4(worldMatrix);

          // Sadece farklı noktaları ekle
          if (positions.length === 0 || !positions[0].equals(p1)) {
            positions.push(p1);
          }
          if (positions.length < 2 || !positions[positions.length - 1].equals(p2)) {
            positions.push(p2);
          }
        }
      }
    });

    // console.log('[OlcumDuzenleDialog] Children\'den bulunan pozisyonlar:', positions.length);

    if (positions.length >= 2) {
      // En uzak iki noktayı bul
      let maxDist = 0;
      let bestP1 = positions[0];
      let bestP2 = positions[1];

      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const dist = positions[i].distanceTo(positions[j]);
          if (dist > maxDist) {
            maxDist = dist;
            bestP1 = positions[i];
            bestP2 = positions[j];
          }
        }
      }

      this.pozisyon1 = { x: bestP1.x, y: bestP1.y, z: bestP1.z };
      this.pozisyon2 = { x: bestP2.x, y: bestP2.y, z: bestP2.z };

      // userData'ya kaydet
      this.olcumGrubu.userData.startPoint = bestP1.clone();
      this.olcumGrubu.userData.endPoint = bestP2.clone();
    }
  }

  private mesafeHesapla(): number {
    const p1 = new THREE.Vector3(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
    const p2 = new THREE.Vector3(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);
    return p1.distanceTo(p2);
  }

  /**
   * 🎯 v3.2.0: Mevcut geometry'den offset değerini hesapla
   * Dimension line ile orijinal noktalar arasındaki mesafeyi bulur
   */
  private offsetHesapla(): number | null {
    if (!this.olcumGrubu) return null;

    const pointA = new THREE.Vector3(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
    const pointB = new THREE.Vector3(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);
    const lineCenter = new THREE.Vector3().lerpVectors(pointA, pointB, 0.5);

    let dimensionLineCenter: THREE.Vector3 | null = null;

    // Dimension line'ın merkezini bul (ilk çizgi genellikle main line)
    this.olcumGrubu.traverse((child) => {
      if (dimensionLineCenter) return;

      // LineSegments2 veya Mesh+LineGeometry
      if (this.isLineBased(child)) {
        const geo = (child as any).geometry;
        if (geo) {
          // LineGeometry için instanceStart attribute'u kullan
          const instanceStart = geo.getAttribute?.('instanceStart');
          const instanceEnd = geo.getAttribute?.('instanceEnd');

          if (instanceStart && instanceEnd && instanceStart.count >= 1) {
            const p1 = new THREE.Vector3(
              instanceStart.getX(0),
              instanceStart.getY(0),
              instanceStart.getZ(0)
            );
            const p2 = new THREE.Vector3(
              instanceEnd.getX(0),
              instanceEnd.getY(0),
              instanceEnd.getZ(0)
            );
            dimensionLineCenter = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
          }
          // Normal BufferGeometry için position attribute kullan
          else {
            const posAttr = geo.getAttribute?.('position');
            if (posAttr && posAttr.count >= 2) {
              const p1 = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
              const p2 = new THREE.Vector3(posAttr.getX(1), posAttr.getY(1), posAttr.getZ(1));
              dimensionLineCenter = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
            }
          }
        }
      }
    });

    if (dimensionLineCenter) {
      const offset = lineCenter.distanceTo(dimensionLineCenter);
      // console.log('[OlcumDuzenleDialog] Offset hesaplandı:', offset);
      return offset > 0.1 ? offset : 15; // Minimum 15
    }

    return null;
  }

  private orijinalSpriteScaleKaydet(): void {
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite && !this.orijinalSpriteScale) {
        this.orijinalSpriteScale = child.scale.clone();
      }
    });
  }

  private orijinalDegerleriKaydet(): void {
    this.orijinalDegerler = {
      isim: this.isim,
      olcumDegeri: this.olcumDegeri,
      birim: this.birim,
      ondalikBasamak: this.ondalikBasamak,
      gorunur: this.gorunur,
      cizgiRengi: this.cizgiRengi,
      okRengi: this.okRengi,
      metinRengi: this.metinRengi,
      metinArkaplanRengi: this.metinArkaplanRengi,
      metinArkaplanSaydamlik: this.metinArkaplanSaydamlik,
      cizgiKalinligi: this.cizgiKalinligi,
      metinBoyutu: this.metinBoyutu,
      okBoyutu: this.okBoyutu,
      okStili: this.okStili,
      cizgiGorunur: this.cizgiGorunur,
      okGorunur: this.okGorunur,
      metinGorunur: this.metinGorunur,
      uzatmaCizgisiGorunur: this.uzatmaCizgisiGorunur,
      glowEfekti: this.glowEfekti,
      glowYogunluk: this.glowYogunluk,
      golgeEfekti: this.golgeEfekti
    };
  }

  // ==================== DEĞİŞİKLİK HANDLERLERİ ====================

  isimDegisti(): void {
    this.olcumGrubu.userData.olcumIsim = this.isim;
    this.olcumGrubu.name = this.isim || `Ölçüm_${this.formatliDegerAl()}`;
    this.sahneYenile();
  }

  gorunurlukDegisti(): void {
    this.olcumGrubu.visible = this.gorunur;
    this.sahneYenile();
  }

  olcumDegeriDegisti(): void {
    const yeniBaslik = this.formatliDegerAl();

    // userData güncelle
    // 🎯 v3.3.0: Koordinat türü için farklı güncelleme
    if (this.olcumTuru === 'koordinat') {
      // Koordinat için yaziParams'ı güncelle (olcumDegeri kullanılmaz)
      if (this.olcumGrubu.userData.yaziParams) {
        this.olcumGrubu.userData.yaziParams.metin = yeniBaslik;
      }
      this.olcumGrubu.userData.olcumBaslik = yeniBaslik;
      if (this.olcumGrubu.userData.FikoArayuz) {
        this.olcumGrubu.userData.FikoArayuz.baslik = yeniBaslik;
      }
      this.labelYenidenOlustur(yeniBaslik);
      this.sahneYenile();
      return;
    }

    // 🎯 v3.2.9: Gerçek değeri AYRI alanda sakla (gercekDeger) - dialog tekrar açıldığında buradan okunacak
    this.olcumGrubu.userData.gercekDeger = this.olcumDegeri;  // 🎯 ASIL GERÇEK DEĞER
    this.olcumGrubu.userData.value = this.olcumDegeri;
    this.olcumGrubu.userData.distance = this.olcumDegeri;
    this.olcumGrubu.userData.unit = this.birim;
    this.olcumGrubu.userData.ondalikBasamak = this.ondalikBasamak;
    this.olcumGrubu.userData.olcumBaslik = yeniBaslik;

    if (this.olcumGrubu.userData.FikoArayuz) {
      this.olcumGrubu.userData.FikoArayuz.baslik = yeniBaslik;
    }

    this.labelYenidenOlustur(yeniBaslik);
    this.sahneYenile();
  }

  formatliDegerAl(): string {
    // 🎯 v3.3.0: Koordinat türü için farklı format
    if (this.olcumTuru === 'koordinat') {
      // Koordinat için mevcut metni yaziParams'tan al veya noktadan oluştur
      const userData = this.olcumGrubu?.userData || {};
      if (userData.yaziParams?.metin) {
        return userData.yaziParams.metin;
      }
      // Koordinat noktasından metin oluştur (satır başı ile)
      if (this.koordinatNoktasi) {
        const x = this.koordinatNoktasi.x.toFixed(this.ondalikBasamak);
        const y = this.koordinatNoktasi.y.toFixed(this.ondalikBasamak);
        const z = this.koordinatNoktasi.z.toFixed(this.ondalikBasamak);
        return `X: ${x}\nY: ${y}\nZ: ${z}`;
      }
      return 'Koordinat';
    }
    return this.olcumDegeri.toFixed(this.ondalikBasamak) + ' ' + this.birim;
  }

  cizgiRengiDegisti(): void {
    const renk = new THREE.Color(this.cizgiRengi);
    this.olcumGrubu.userData.cizgiRengi = this.cizgiRengi;

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line) {
        const mat = child.material as THREE.LineBasicMaterial;
        if (mat?.color) {
          mat.color.copy(renk);
          mat.needsUpdate = true;
        }
      }
    });

    this.sahneYenile();
  }

  okRengiDegisti(): void {
    const renk = new THREE.Color(this.okRengi);
    this.olcumGrubu.userData.okRengi = this.okRengi;

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material as THREE.MeshBasicMaterial;
        if (mat?.color) {
          mat.color.copy(renk);
          mat.needsUpdate = true;
        }
      }
    });

    this.sahneYenile();
  }

  metinRengiDegisti(): void {
    this.olcumGrubu.userData.textColor = this.metinRengi;
    this.olcumGrubu.userData.metinRengi = this.metinRengi;
    this.labelYenidenOlustur(this.formatliDegerAl());
    this.sahneYenile();
  }

  metinArkaplanDegisti(): void {
    this.olcumGrubu.userData.metinArkaplanRengi = this.metinArkaplanRengi;
    this.olcumGrubu.userData.metinArkaplanSaydamlik = this.metinArkaplanSaydamlik;
    this.labelYenidenOlustur(this.formatliDegerAl());
    this.sahneYenile();
  }

  metinBoyutuDegisti(): void {
    this.olcumGrubu.userData.fontSize = this.metinBoyutu;
    this.labelYenidenOlustur(this.formatliDegerAl());
    this.sahneYenile();
  }

  cizgiKalinligiDegisti(): void {
    this.olcumGrubu.userData.lineWidth = this.cizgiKalinligi;

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line) {
        const mat = child.material as THREE.LineBasicMaterial;
        if (mat) {
          mat.linewidth = this.cizgiKalinligi;
          mat.needsUpdate = true;
        }
      }
    });

    this.sahneYenile();
  }

  okBoyutuDegisti(): void {
    this.olcumGrubu.userData.okBoyutu = this.okBoyutu;
    // Ok mesh'lerinin scale'ini güncelle
    const scale = this.okBoyutu / 8;
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.scale.setScalar(scale);
      }
    });
    this.sahneYenile();
  }

  okStiliDegisti(): void {
    this.olcumGrubu.userData.okStili = this.okStili;
    // Ok stilini değiştirmek için mesh'leri yeniden oluşturmak gerekir
    // Şimdilik sadece userData'ya kaydediyoruz
    this.sahneYenile();
  }

  cizgiGorunurlukDegisti(): void {
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line && !child.name?.includes('uzatma') && !child.userData?.isExtension) {
        child.visible = this.cizgiGorunur;
      }
    });
    this.sahneYenile();
  }

  okGorunurlukDegisti(): void {
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.visible = this.okGorunur;
      }
    });
    this.sahneYenile();
  }

  metinGorunurlukDegisti(): void {
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite) {
        child.visible = this.metinGorunur;
      }
    });
    this.sahneYenile();
  }

  uzatmaCizgisiGorunurlukDegisti(): void {
    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Line && (child.name?.includes('uzatma') || child.userData?.isExtension)) {
        child.visible = this.uzatmaCizgisiGorunur;
      }
    });
    this.sahneYenile();
  }

  glowEfektiDegisti(): void {
    this.olcumGrubu.userData.glowEfekti = this.glowEfekti;
    this.olcumGrubu.userData.glowYogunluk = this.glowYogunluk;
    this.labelYenidenOlustur(this.formatliDegerAl());
    this.sahneYenile();
  }

  golgeEfektiDegisti(): void {
    this.olcumGrubu.userData.golgeEfekti = this.golgeEfekti;
    this.labelYenidenOlustur(this.formatliDegerAl());
    this.sahneYenile();
  }

  /**
   * 🎯 v3.2.3: Sabit boyut değiştiğinde
   * Yazı boyutunun zoom'dan etkilenip etkilenmeyeceğini kontrol eder
   */
  sabitBoyutDegisti(): void {
    // console.log('[OlcumDuzenleDialog] sabitBoyut değiştiriliyor:', this.sabitBoyut);

    // userData'ya kaydet
    this.olcumGrubu.userData.sabitBoyut = this.sabitBoyut;

    // Eğer olcumGrubu'nun sabitBoyut property'si varsa (LinearDimension veya FikoOlcumCizgisi)
    if ((this.olcumGrubu as any).sabitBoyut !== undefined) {
      (this.olcumGrubu as any).sabitBoyut = this.sabitBoyut;
      // console.log('[OlcumDuzenleDialog] olcumGrubu.sabitBoyut ayarlandı:', (this.olcumGrubu as any).sabitBoyut);
    }

    // FikoOlcumCizgisi ise kendi kameraYonDegisti metodunu çağır
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      if (this.sabitBoyut) {
        // Sabit boyut açıldı - kamera scale'i uygula
        (this.olcumGrubu as any).kameraYonDegisti?.(this.olcumGrubu);
      } else {
        // Sabit boyut kapatıldı - orijinal scale'e dön
        const yazi = (this.olcumGrubu as any).yazi;
        if (yazi) {
          const canvas = yazi.material?.map?.image as HTMLCanvasElement;
          if (canvas) {
            yazi.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
            yazi.userData.originalScale = yazi.scale.clone();
          }
        }
      }
    } else if ((this.olcumGrubu as any).sabitBoyutGuncelle) {
      // LinearDimension - kendi metodunu çağır
      (this.olcumGrubu as any).sabitBoyutGuncelle();
    } else {
      // Plain Group
      if (this.sabitBoyut) {
        // Sabit boyut açıldı - kamera scale'i uygula
        this.labelYenidenOlustur(this.formatliDegerAl());
      } else {
        // Sabit boyut kapatıldı - label'ı base scale'e döndür
        this.olcumGrubu.traverse((child) => {
          if (child instanceof THREE.Sprite) {
            // Base scale'i yeniden hesapla (kamera scale'siz)
            const canvas = child.material.map?.image as HTMLCanvasElement;
            if (canvas) {
              child.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
              child.userData.originalScale = child.scale.clone();
            }
          }
        });
      }
    }

    this.sahneYenile();
    // console.log('[OlcumDuzenleDialog] sabitBoyut değişti:', this.sabitBoyut);
  }

  tumRenkleriAyniYap(): void {
    this.okRengi = this.cizgiRengi;
    this.okRengiDegisti();
  }

  // ==================== LABEL OLUŞTURMA ====================

  /**
   * 🎯 v3.2.1: LinearDimension ile UYUMLU label yeniden oluşturma
   * 🎯 v3.3.0: FikoOlcumCizgisi için yaziEkle kullan, tüm sprite'ları temizle
   */
  private labelYenidenOlustur(metin: string): void {
    // 🎯 v3.3.0: FikoOlcumCizgisi ise onun yaziEkle metodunu kullan
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      const fikoOlcum = this.olcumGrubu as FikoOlcumCizgisi;

      // 🎯 v3.3.6 FIX: Klonlanmış ölçümde this.yazi null - children'dan originalScale'i al
      // yaziEkle çağrılmadan ÖNCE originalScale'i kaydet (büyüme birikimini önle)
      if (!fikoOlcum.yaziSprite) {
        // Klonlanmış - children'dan sprite bul ve originalScale'i kaydet
        fikoOlcum.children.forEach((child: THREE.Object3D) => {
          if (child instanceof THREE.Sprite && child.userData?.originalScale) {
            fikoOlcum.userData._oncekiOriginalScale = (child.userData.originalScale as THREE.Vector3).clone();
          }
        });
      }

      // yaziParams'tan renk bilgilerini al veya dialog değerlerini kullan
      const yaziParams = this.olcumGrubu.userData.yaziParams;
      const textColor = yaziParams?.textColor || this.metinRengi;
      const bgColor = yaziParams?.backgroundColor || this.metinArkaplanRengi;
      const bgAlpha = (yaziParams?.backgroundAlpha !== undefined) ? yaziParams.backgroundAlpha : (this.metinArkaplanSaydamlik / 100);

      fikoOlcum.yaziEkle(
        metin,
        undefined,
        this.metinBoyutu,
        textColor,
        bgColor,
        bgAlpha
      );

      // 🎯 v3.3.6 FIX: kameraYonDegisti çağırmıyoruz - yaziEkle zaten scale'i koruyor
      // kameraYonDegisti çağrıldığında scale yeniden hesaplanıyor ve büyüme sorunu oluşuyor
      return;
    }

    // 🎯 v3.3.0: Tüm sprite'ları bul ve temizle (birden fazla olabilir)
    const spriteList: THREE.Sprite[] = [];
    let spritePosition = new THREE.Vector3();

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite) {
        spriteList.push(child);
        spritePosition.copy(child.position); // Son sprite'ın pozisyonunu al
      }
    });

    // Tüm eski sprite'ları sil
    for (const sprite of spriteList) {
      this.olcumGrubu.remove(sprite);
      const mat = sprite.material;
      mat.map?.dispose();
      mat.dispose();
    }

    // Hiç sprite yoksa çık
    if (spriteList.length === 0) {
      return;
    }

    // 🎯 v3.2.3: Yeni sprite oluştur
    const yeniSprite = this.canvasLabelOlustur(metin);
    yeniSprite.position.copy(spritePosition);

    // 🎯 v3.2.4: Base scale hesaplama ve ÖNCE originalScale kaydet
    const canvas = yeniSprite.material.map?.image as HTMLCanvasElement;
    if (canvas) {
      yeniSprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
    }

    // 🎯 v3.2.4: originalScale = BASE scale (kamera scale'siz)
    // FikoKaliciOlcumler.tumOlcumlereSabitBoyutUygula() bu değeri kullanır
    yeniSprite.userData.originalScale = yeniSprite.scale.clone();
    yeniSprite.userData.isLabel = true;

    // 🎯 v3.2.4: Kamera scale uygula - SADECE SABİT BOYUT AÇIKSA
    // originalScale kaydedildikten SONRA uygulanır
    if (this.sabitBoyut && this.anaGosterim?.aktifKamera) {
      const kamera = this.anaGosterim.aktifKamera as any;
      const zoom = kamera?.zoom ?? 1;
      const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
      const scaleAdjustment = factor * 0.02;
      yeniSprite.scale.multiplyScalar(scaleAdjustment);
    }

    this.olcumGrubu.add(yeniSprite);
  }

  /**
   * 🎯 v3.2.1: LinearDimension ile UYUMLU label oluşturma
   * sizeAttenuation: true kullanır (LinearDimension ile aynı)
   * 🎯 v3.3.0: Çoklu satır desteği eklendi (koordinat için)
   */
  private canvasLabelOlustur(metin: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // 🎯 v3.2.1: LinearDimension ile AYNI font boyutu hesaplama
    const baseFontSize = this.metinBoyutu || 14;
    const mainFontSize = baseFontSize * 4; // LinearDimension: fontSize * 4
    const fontFamily = "'Segoe UI', Arial, sans-serif";
    const font = `bold ${mainFontSize}px ${fontFamily}`;
    ctx.font = font;

    // 🎯 v3.3.0: Çoklu satır desteği
    const satirlar = metin.split('\n');
    const satirSayisi = satirlar.length;
    const satirYuksekligi = mainFontSize * 1.2;

    // Her satır için genişlik ölç, en geniş olanı kullan
    let maxWidth = 0;
    for (const satir of satirlar) {
      const metrics = ctx.measureText(satir);
      if (metrics.width > maxWidth) {
        maxWidth = metrics.width;
      }
    }

    // 🎯 v3.2.1: LinearDimension ile AYNI padding hesaplama
    const paddingX = 12;
    const paddingY = 6;
    canvas.width = Math.ceil(maxWidth + paddingX * 2);
    canvas.height = Math.ceil(satirYuksekligi * satirSayisi + paddingY * 2);

    // 🎯 v3.2.1: LinearDimension ile AYNI arkaplan çizimi (düz dikdörtgen)
    ctx.fillStyle = this.metinArkaplanRengi;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Font tekrar ayarla
    ctx.font = font;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // 🎯 v3.3.0: Her satırı ayrı çiz
    ctx.fillStyle = this.metinRengi;
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    for (let i = 0; i < satirlar.length; i++) {
      const y = paddingY + satirYuksekligi * (i + 0.5);
      ctx.fillText(satirlar[i], paddingX, y);
    }

    // Texture
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    // 🎯 v3.2.1: LinearDimension ile AYNI material ayarları
    // sizeAttenuation: true (kamera zoom'u ile birlikte scale edilir, sonra kamera event ile düzeltilir)
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      sizeAttenuation: true,  // 🎯 LinearDimension ile AYNI
      toneMapped: false
    });

    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 999999;
    sprite.frustumCulled = false;

    // 🎯 v3.2.1: LinearDimension ile AYNI scale hesaplama
    sprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);

    // 🎯 v3.2.1: originalScale kaydet (sabit boyut sistemi için)
    sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);
    sprite.userData.textColor = this.metinRengi;
    sprite.userData.backgroundColor = this.metinArkaplanRengi;
    sprite.userData.fontSize = baseFontSize;

    return sprite;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // ==================== TAŞIMA KONTROLÜ - DraggableSupport.ts BİREBİR KOPYASI ====================

  /**
   * 🎯 Taşıma modunu aç/kapat (toggle)
   */
  tasimaModuToggle(): void {
    if (this.tasimaModu) {
      this.tasimaModuKapat();
    } else {
      this.tasimaModuAc();
    }
  }

  /**
   * 🎯 Taşıma modunu aç - DraggableSupport.attach() birebir aynısı
   * Plain Group ve FikoOlcumCizgisi için çalışır
   * 🎯 v3.3.4: Koordinat ölçümü için FikoOlcumCizgisi sürükleme sistemi kullanılır
   */
  tasimaModuAc(): void {
    if (!this.anaGosterim || !this.olcumGrubu) {
      console.warn('[OlcumDuzenleDialog] anaGosterim veya olcumGrubu yok');
      return;
    }

    const threeRenderer = this.anaGosterim.sunucu;
    const kamera = this.anaGosterim.aktifKamera;
    const sahne = this.anaGosterim.sahneler?.[0];

    if (!threeRenderer?.domElement || !kamera || !sahne) {
      console.warn('[OlcumDuzenleDialog] Canvas, kamera veya sahne bulunamadı');
      return;
    }

    this.canvasElement = threeRenderer.domElement as HTMLCanvasElement;

    // 🎯 FikoOlcumCizgisi ise kendi sürükleme sistemini kullan
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      // Orijinal offset'i kaydet
      this.orijinalOffset = this.olcumGrubu.offsetMesafe;

      // draggable'ı aktif et
      this.olcumGrubu.draggable = true;

      // Sürüklemeyi başlat - AYNI ekleme diyaloğundaki gibi
      this.olcumGrubu.suruklemeyiBaslat(this.canvasElement, kamera, sahne);

      // console.log('[OlcumDuzenleDialog] FikoOlcumCizgisi sürükleme aktif');
    } else {
      // 🎯 v3.0.2: Plain Group için LinearDimension tarzı sürükleme sistemi
      // pointA ve pointB SABİT KALIR, sadece dimension görüntüsü değişir
      // 🎯 v3.0.2: pointA ve pointB'yi al ve kaydet
      this.extractPlainGroupPoints();

      // 🎯 v3.0.2: Mevcut offset'i hesapla (dimension line ile orijinal noktalar arası mesafe)
      this.calculateCurrentOffset();

      // 🎯 v3.0.2: Orijinal değerleri kaydet (sıfırlama için)
      this.plainGroupOrijinalOffset = this.plainGroupOffset;
      this.plainGroupOrijinalRotation = this.plainGroupRotation;

      // 🎯 Offset yönünü hesapla (pointA ve pointB'den)
      this.calculateOffsetDirection();

      // 🚀 Bound handlers (bind once, reuse) - DraggableSupport satır 106-111
      this.boundOnPointerDown = this.onPointerDown.bind(this);
      this.boundOnPointerMove = this.onPointerMove.bind(this);
      this.boundOnPointerUp = this.onPointerUp.bind(this);
      this.boundOnPointerCancel = this.onPointerUp.bind(this);
      // 🎯 v3.2.12: Keyboard handlers
      this.boundOnKeyUp = this.onKeyUp.bind(this);
      this.boundOnKeyDown = this.onKeyDown.bind(this);

      // 🎯 FIX: Tüm listener'lar passive: false olmalı (stopPropagation için)
      // Capture phase kullanarak diğer handler'lardan önce yakala
      // DraggableSupport satır 124-129
      this.canvasElement.addEventListener('pointerdown', this.boundOnPointerDown, { passive: false, capture: true });
      this.canvasElement.addEventListener('pointermove', this.boundOnPointerMove, { passive: false, capture: true });
      this.canvasElement.addEventListener('pointerup', this.boundOnPointerUp, { passive: false, capture: true });
      this.canvasElement.addEventListener('pointercancel', this.boundOnPointerCancel, { passive: false, capture: true });
      this.canvasElement.addEventListener('pointerleave', this.boundOnPointerUp, { passive: false, capture: true });

      // 🎯 v3.2.12: Keyboard event'leri window'a ekle (Shift tuşu için)
      window.addEventListener('keyup', this.boundOnKeyUp);
      window.addEventListener('keydown', this.boundOnKeyDown);

      // Cursor'u değiştir
      this.canvasElement.style.cursor = 'grab';

      // console.log('[OlcumDuzenleDialog] v3.0.2 Plain Group sürükleme aktif - offset:', this.plainGroupOffset.toFixed(2), 'rotation:', this.plainGroupRotation.toFixed(2), '(Shift=Döndür)');
    }

    this.tasimaModu = true;
    // console.log('[OlcumDuzenleDialog] Taşıma modu açıldı');
  }

  /**
   * 🎯 Taşıma modunu kapat - DraggableSupport.detach() birebir aynısı
   */
  tasimaModuKapat(): void {
    // FikoOlcumCizgisi ise sürüklemeyi durdur
    if (this.olcumGrubu instanceof FikoOlcumCizgisi && this.canvasElement) {
      this.olcumGrubu.suruklemeviDurdur(this.canvasElement);
    } else if (this.canvasElement) {
      // 🎯 FIX: capture: true ile eklendiyse, kaldırırken de capture: true olmalı
      // DraggableSupport satır 138-143
      if (this.boundOnPointerDown) {
        this.canvasElement.removeEventListener('pointerdown', this.boundOnPointerDown, { capture: true });
      }
      if (this.boundOnPointerMove) {
        this.canvasElement.removeEventListener('pointermove', this.boundOnPointerMove, { capture: true });
      }
      if (this.boundOnPointerUp) {
        this.canvasElement.removeEventListener('pointerup', this.boundOnPointerUp, { capture: true });
        this.canvasElement.removeEventListener('pointercancel', this.boundOnPointerUp, { capture: true });
        this.canvasElement.removeEventListener('pointerleave', this.boundOnPointerUp, { capture: true });
      }

      // 🎯 v3.2.12: Keyboard event'lerini kaldır
      if (this.boundOnKeyUp) {
        window.removeEventListener('keyup', this.boundOnKeyUp);
      }
      if (this.boundOnKeyDown) {
        window.removeEventListener('keydown', this.boundOnKeyDown);
      }

      // Cursor'u sıfırla
      this.canvasElement.style.cursor = '';

      // State temizle
      this.dragState.active = false;
      this.dragState.mode = this.DRAG_MODE_OFFSET;
      // 🎯 v3.2.6: Offset yönünü sıfırla (sonraki açılışta yeniden hesaplansın)
      this.dragState.offsetDirection.set(0, 0, 0);
      this.boundOnPointerDown = null;
      this.boundOnPointerMove = null;
      this.boundOnPointerUp = null;
      this.boundOnPointerCancel = null;
      this.boundOnKeyUp = null;
      this.boundOnKeyDown = null;

      // RAF temizle - DraggableSupport satır 148-151
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      // console.log('[OlcumDuzenleDialog] Plain Group sürükleme event listener\'ları temizlendi');
    }

    this.canvasElement = null;
    this.tasimaModu = false;
    // console.log('[OlcumDuzenleDialog] Taşıma modu kapatıldı');
  }

  // ==================== POINTER EVENT HANDLERS - DraggableSupport.ts BİREBİR KOPYASI ====================

  /**
   * Pointer down handler - v3.0.1: LineSegments2 desteği ile
   */
  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return; // Sadece sol tık

    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera || !this.canvasElement) return;

    // Mouse pozisyonunu normalize et
    const mouse = this.getNormalizedMouse(event);

    // 🎯 v3.0.1: Çizgi threshold'unu ayarla (ince çizgilere tıklamayı kolaylaştır)
    OlcumDuzenleDialogComponent._raycaster.params.Line = { threshold: 5 };

    // Hit test - olcumGrubu'nun children'larına tıklandı mı?
    OlcumDuzenleDialogComponent._raycaster.setFromCamera(mouse, kamera);

    // 🎯 v3.0.1: Önce normal intersect dene
    let intersects = OlcumDuzenleDialogComponent._raycaster.intersectObjects(this.olcumGrubu.children, true);

    // 🎯 v3.0.1: LineSegments2 için özel kontrol (raycast desteklemiyor olabilir)
    if (intersects.length === 0) {
      // Bounding box kontrolü - dimension grubunun bounding box'ına tıklandı mı?
      const box = new THREE.Box3().setFromObject(this.olcumGrubu);
      const ray = OlcumDuzenleDialogComponent._raycaster.ray;

      if (ray.intersectsBox(box)) {
        // Bounding box'a tıklandı - sürüklemeyi başlat
        intersects = [{ distance: 0, point: new THREE.Vector3(), object: this.olcumGrubu }] as any;
      }
    }

    if (intersects.length > 0) {
      // 🎯 FIX: Event'i tamamen durdur - FikoSecililer'e ulaşmasın
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // 🎯 v2.0.2: Shift tuşu ile döndürme modu
      const mode = event.shiftKey ? this.DRAG_MODE_ROTATE : this.DRAG_MODE_OFFSET;

      // Drag başlat
      this.startDrag(mouse, mode);
    }
  }

  /**
   * Pointer move handler - DraggableSupport satır 186-219
   */
  private onPointerMove(event: PointerEvent): void {
    // 🎯 FIX: Sürükleme aktifken event'i durdur
    if (this.dragState.active) {
      event.preventDefault();
      event.stopPropagation();
    }

    const now = performance.now();

    // 🚀 THROTTLE: 60fps cap - DraggableSupport satır 196-214
    if (now - this.lastMoveTime < this.MOVE_THROTTLE_MS) {
      // Pending mouse kaydet (RAF'ta işlenecek)
      if (!this.pendingMouse) {
        this.pendingMouse = new THREE.Vector2();
      }
      this.pendingMouse.set(event.clientX, event.clientY);
      // 🎯 v2.0.3: ShiftKey durumunu da kaydet
      this.pendingShiftKey = event.shiftKey;

      // RAF schedule et
      if (this.rafId === null) {
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          if (this.pendingMouse) {
            this.processPendingMove();
          }
        });
      }
      return;
    }

    this.lastMoveTime = now;
    this.processMove(event);
  }

  /**
   * Bekleyen move'u işle - DraggableSupport satır 225-244
   * 🎯 v2.0.3: pendingShiftKey de kullanılıyor
   */
  private processPendingMove(): void {
    if (!this.pendingMouse || !this.canvasElement) return;

    // Fake event oluştur
    const rect = this.canvasElement.getBoundingClientRect();
    const mouse = OlcumDuzenleDialogComponent._tempVec2.set(
      ((this.pendingMouse.x - rect.left) / rect.width) * 2 - 1,
      -((this.pendingMouse.y - rect.top) / rect.height) * 2 + 1
    );

    // 🎯 v2.0.3: ShiftKey durumunu al ve sıfırla
    const shiftKey = this.pendingShiftKey;
    this.pendingMouse = null;
    this.pendingShiftKey = false;

    if (this.dragState.active) {
      this.updateDrag(mouse, shiftKey);
    }
  }

  /**
   * Move işle - DraggableSupport satır 251-259
   * 🎯 v2.0.3: ShiftKey'i updateDrag'e ilet
   * 🎯 v3.2.12: lastMousePosition'ı güncelle (keyup event için)
   */
  private processMove(event: PointerEvent): void {
    const mouse = this.getNormalizedMouse(event);

    // 🎯 v3.2.12: Son mouse pozisyonunu kaydet (Shift keyup event'i için)
    this.lastMousePosition.copy(mouse);

    if (this.dragState.active) {
      this.updateDrag(mouse, event.shiftKey);
    }
  }

  /**
   * Pointer up handler - DraggableSupport satır 264-274
   */
  private onPointerUp(event: PointerEvent): void {
    if (this.dragState.active) {
      // 🎯 FIX: Sürükleme aktifken event'i durdur
      // Bu sayede FikoSecililer'in tiklandi event'i tetiklenmez
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.endDrag();
    }
  }

  /**
   * 🎯 v3.2.12: Keyboard up handler - Shift bırakıldığında mod değiştir
   */
  private onKeyUp(event: KeyboardEvent): void {
    // Sadece Shift tuşu ve aktif sürükleme varsa
    if (event.key === 'Shift' && this.dragState.active) {
      // ROTATE modundan OFFSET moduna geç
      this.updateDrag(this.lastMousePosition, false);
    }
  }

  /**
   * 🎯 v3.2.12: Keyboard down handler - Shift basıldığında mod değiştir
   */
  private onKeyDown(event: KeyboardEvent): void {
    // Sadece Shift tuşu ve aktif sürükleme varsa
    if (event.key === 'Shift' && this.dragState.active) {
      // OFFSET modundan ROTATE moduna geç
      this.updateDrag(this.lastMousePosition, true);
    }
  }

  /**
   * Normalized mouse pozisyonu al - DraggableSupport satır 496-506
   */
  private getNormalizedMouse(event: PointerEvent): THREE.Vector2 {
    if (!this.canvasElement) {
      return OlcumDuzenleDialogComponent._tempVec2.set(0, 0);
    }

    const rect = this.canvasElement.getBoundingClientRect();
    return OlcumDuzenleDialogComponent._tempVec2.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  }

  // ==================== DRAG METHODS - DraggableSupport.ts BİREBİR KOPYASI ====================

  /**
   * 🎯 Offset yönünü hesapla (userData'dan veya geometriden)
   * DraggableSupport satır 314-317 tarzında ama Plain Group için
   * 🎯 v3.2.5: calculateRotatedOffsetDirection() ile tutarlı - y<0 kontrolü eklendi
   * 🎯 v3.2.6 FIX: Eğer calculateCurrentOffset() zaten gerçek yönü hesapladıysa, atla
   * 🎯 v3.2.11: forceRecalculate parametresi - döndürme sonrası yeniden hesaplama için
   */
  private calculateOffsetDirection(forceRecalculate: boolean = false): void {
    // 🎯 v3.2.6: Eğer offset yönü zaten hesaplandıysa (calculateCurrentOffset tarafından), atla
    // 🎯 v3.2.11: forceRecalculate=true ise bu kontrolü atla (döndürme sonrası yeniden hesaplama)
    if (!forceRecalculate && this.dragState.offsetDirection.lengthSq() > 0.0001) {
      // console.log('[OlcumDuzenleDialog] v3.2.6 Offset yönü zaten hesaplanmış, atlanıyor');
      return;
    }

    // 1. Önce userData'dan dene
    const userData = this.olcumGrubu.userData;
    if (userData.startPoint && userData.endPoint) {
      const p1 = new THREE.Vector3(userData.startPoint.x, userData.startPoint.y, userData.startPoint.z);
      const p2 = new THREE.Vector3(userData.endPoint.x, userData.endPoint.y, userData.endPoint.z);

      // Noktalar arasındaki vektöre dik yön
      const lineDir = p2.clone().sub(p1).normalize();
      // Varsayılan up vektörü (Y yukarı)
      const up = new THREE.Vector3(0, 1, 0);
      // Cross product ile dik yön
      this.dragState.offsetDirection.crossVectors(lineDir, up).normalize();

      // Eğer çizgi Y eksenine paralelse, farklı bir up kullan
      if (this.dragState.offsetDirection.lengthSq() < 0.001) {
        up.set(1, 0, 0);
        this.dragState.offsetDirection.crossVectors(lineDir, up).normalize();
      }

      // 🎯 v3.2.5: Her zaman yukarı doğru olsun (tutarlılık için)
      if (this.dragState.offsetDirection.y < 0) {
        this.dragState.offsetDirection.negate();
      }

      // console.log('[OlcumDuzenleDialog] v3.2.5 Offset yönü hesaplandı (userData):', this.dragState.offsetDirection);
      return;
    }

    // 2. Pozisyon1 ve pozisyon2'den dene
    if (this.pozisyon1 && this.pozisyon2) {
      const p1 = new THREE.Vector3(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
      const p2 = new THREE.Vector3(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);

      const lineDir = p2.clone().sub(p1).normalize();
      const up = new THREE.Vector3(0, 1, 0);
      this.dragState.offsetDirection.crossVectors(lineDir, up).normalize();

      if (this.dragState.offsetDirection.lengthSq() < 0.001) {
        up.set(1, 0, 0);
        this.dragState.offsetDirection.crossVectors(lineDir, up).normalize();
      }

      // 🎯 v3.2.5: Her zaman yukarı doğru olsun (tutarlılık için)
      if (this.dragState.offsetDirection.y < 0) {
        this.dragState.offsetDirection.negate();
      }

      // console.log('[OlcumDuzenleDialog] v3.2.5 Offset yönü hesaplandı (pozisyonlar):', this.dragState.offsetDirection);
      return;
    }

    // 3. Varsayılan: Y yukarı
    this.dragState.offsetDirection.set(0, 1, 0);
    // console.log('[OlcumDuzenleDialog] v3.2.5 Offset yönü varsayılan Y yukarı');
  }

  /**
   * Drag başlat - v3.0.0: LinearDimension tarzı geometry rebuild desteği
   * 🎯 v3.0.0: pointA/pointB sabit kalır, offset/rotation değişir
   */
  private startDrag(mouse: THREE.Vector2, mode: string = this.DRAG_MODE_OFFSET): void {
    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera) return;

    // Drag plane oluştur (kameraya paralel)
    const cameraDirection = OlcumDuzenleDialogComponent._tempVec3.set(0, 0, -1);
    cameraDirection.applyQuaternion(kamera.quaternion);

    // Dimension'ın merkez noktasını al (pointA ve pointB ortası)
    const dimensionCenter = new THREE.Vector3().lerpVectors(this.plainGroupPointA, this.plainGroupPointB, 0.5);
    this.dragState.plane.setFromNormalAndCoplanarPoint(cameraDirection, dimensionCenter);

    // Başlangıç değerlerini kaydet
    this.dragState.active = true;
    this.dragState.startMouse.copy(mouse);
    this.dragState.mode = mode;

    // 🎯 v3.0.0: Mevcut offset ve rotation değerlerini kaydet
    this.dragState.startDimensionOffset = this.plainGroupOffset;
    this.dragState.startRotation = this.plainGroupRotation;

    // Cursor değiştir
    if (this.canvasElement) {
      this.canvasElement.style.cursor = 'grabbing';
    }

    // console.log('[OlcumDuzenleDialog] v3.0.2 Sürükleme başladı, mod:', mode, 'offset:', this.plainGroupOffset.toFixed(2), 'rotation:', this.plainGroupRotation.toFixed(2));
  }

  /**
   * Drag güncelle - v3.0.0: LinearDimension tarzı geometry rebuild
   * 🎯 v3.0.0: pointA ve pointB SABİT KALIR, sadece dimension görüntüsü değişir
   * 🎯 v3.0.1: CATIA tarzı - offset değişir, orijinal noktalar sabit
   * 🎯 v3.0.2: Döndürme modu desteği (Shift tuşu)
   */
  private updateDrag(mouse: THREE.Vector2, shiftKey: boolean = false): void {
    if (!this.dragState.active) return;

    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera) return;

    // 🎯 Gerçek zamanlı mod değişikliği - Shift basılıyken döndürme, değilken offset
    const currentMode = shiftKey ? this.DRAG_MODE_ROTATE : this.DRAG_MODE_OFFSET;

    // 🎯 Mod değiştiyse mevcut mouse pozisyonunu yeni başlangıç yap (kesintisiz geçiş)
    if (currentMode !== this.dragState.mode) {
      this.dragState.mode = currentMode;
      this.dragState.startMouse.copy(mouse);
      // Mevcut offset ve rotation değerlerini yeni başlangıç olarak kaydet
      this.dragState.startDimensionOffset = this.plainGroupOffset;
      this.dragState.startRotation = this.plainGroupRotation;

      // 🎯 v3.2.11: ROTATE → OFFSET moduna geçişte offset yönünü yeniden hesapla
      // Döndürme sonrası yön değişmiş olabilir (örn. 180° döndürme)
      if (currentMode === this.DRAG_MODE_OFFSET) {
        // Mevcut rotasyon açısını dikkate alarak yeni offset yönünü hesapla
        const userData = this.olcumGrubu?.userData;
        if (userData?.startPoint && userData?.endPoint) {
          const p1 = new THREE.Vector3(userData.startPoint.x, userData.startPoint.y, userData.startPoint.z);
          const p2 = new THREE.Vector3(userData.endPoint.x, userData.endPoint.y, userData.endPoint.z);
          const direction = p2.clone().sub(p1).normalize();
          // Döndürme açısını uygulayarak offset yönünü hesapla
          const rotatedOffsetDir = this.calculateRotatedOffsetDirection(direction, this.plainGroupRotation);
          this.dragState.offsetDirection.copy(rotatedOffsetDir);
          // console.log('[OlcumDuzenleDialog] v3.2.11 Offset yönü döndürme sonrası yeniden hesaplandı:', rotatedOffsetDir);
        } else if (this.pozisyon1 && this.pozisyon2) {
          const p1 = new THREE.Vector3(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
          const p2 = new THREE.Vector3(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);
          const direction = p2.clone().sub(p1).normalize();
          const rotatedOffsetDir = this.calculateRotatedOffsetDirection(direction, this.plainGroupRotation);
          this.dragState.offsetDirection.copy(rotatedOffsetDir);
          // console.log('[OlcumDuzenleDialog] v3.2.11 Offset yönü döndürme sonrası yeniden hesaplandı (pozisyon):', rotatedOffsetDir);
        }
      }
    }

    // Ray oluştur
    OlcumDuzenleDialogComponent._raycaster.setFromCamera(mouse, kamera);

    // Plane ile kesişim bul
    const intersection = OlcumDuzenleDialogComponent._tempVec3;
    const ray = OlcumDuzenleDialogComponent._raycaster.ray;

    if (ray.intersectPlane(this.dragState.plane, intersection)) {
      // Başlangıç kesişimi
      OlcumDuzenleDialogComponent._raycaster.setFromCamera(this.dragState.startMouse, kamera);
      const startIntersection = OlcumDuzenleDialogComponent._tempVec3_2;

      if (OlcumDuzenleDialogComponent._raycaster.ray.intersectPlane(this.dragState.plane, startIntersection)) {
        // Delta hesapla (3D hareket)
        const delta = intersection.clone().sub(startIntersection);

        if (this.dragState.mode === this.DRAG_MODE_ROTATE) {
          // 🎯 v3.2.9: Basit yatay hareket = döndürme
          // Sağa çek = saat yönünde, sola çek = saat yönü tersi
          const mouseDeltaX = mouse.x - this.dragState.startMouse.x;

          // Hassasiyet: Daha hızlı döndürme (3x)
          const rotationDelta = mouseDeltaX * Math.PI * 3;

          // Yeni açı hesapla
          const newRotation = this.dragState.startRotation + rotationDelta;

          // 🎯 v3.0.0: Geometry rebuild ile döndür (pointA/pointB sabit kalır)
          this.plainGroupRotation = newRotation;
          this.rebuildPlainGroupGeometry();
        } else {
          // 🎯 v3.0.0: OFFSET MODU - LinearDimension.setOffset() tarzı
          // Delta vektörünü offset yönüne projekte et
          const offsetDelta = delta.dot(this.dragState.offsetDirection);

          // 🎯 v3.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
          const newOffset = this.dragState.startDimensionOffset + offsetDelta;

          // 🎯 v3.0.0: Geometry rebuild ile offset değiştir (pointA/pointB sabit kalır)
          this.plainGroupOffset = newOffset;
          this.rebuildPlainGroupGeometry();
        }

        // Sahneyi yenile
        this.anaGosterim?.ciz?.();
      }
    }
  }

  /**
   * Drag bitir - DraggableSupport satır 414-433
   */
  private endDrag(): void {
    // State temizle
    this.dragState.active = false;

    // Cursor normal
    if (this.canvasElement) {
      this.canvasElement.style.cursor = 'grab';
    }

    // console.log('[OlcumDuzenleDialog] Sürükleme bitti');
  }

  /**
   * 🎯 v3.0.0: Pozisyonu/Offset'i sıfırla - orijinal değere geri dön
   * pointA/pointB sabit kalır, offset/rotation orijinal değere döner
   */
  pozisyonuSifirla(): void {
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      this.olcumGrubu.offsetSifirla();
      this.sahneYenile();
      // console.log('[OlcumDuzenleDialog] FikoOlcumCizgisi offset sıfırlandı');
    } else {
      // 🎯 v3.0.0: Plain Group için orijinal offset ve rotation'a dön
      // Geometry rebuild ile - pointA/pointB sabit kalır
      this.plainGroupOffset = this.plainGroupOrijinalOffset;
      this.plainGroupRotation = this.plainGroupOrijinalRotation;
      this.rebuildPlainGroupGeometry();
      this.sahneYenile();
      // console.log('[OlcumDuzenleDialog] v3.0.2 Plain Group offset ve rotation sıfırlandı:', this.plainGroupOffset.toFixed(2), this.plainGroupRotation.toFixed(2));
    }
  }

  // ==================== AKSIYONLAR ====================

  odakla(): void {
    if (!this.anaGosterim?.aktifKontrol) return;

    const box = new THREE.Box3().setFromObject(this.olcumGrubu);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    this.anaGosterim.aktifKontrol.target.copy(center);
    this.anaGosterim.aktifKontrol.update();

    this.sahneYenile();
  }

  kopyala(): void {
    const text = `${this.isim || 'Ölçüm'}: ${this.formatliDegerAl()}`;
    navigator.clipboard.writeText(text).then(() => {
      // console.log('[OlcumDuzenleDialog] Panoya kopyalandı:', text);
    });
  }

  varsayilanaGeriDon(): void {
    // 🎯 v3.2.0: Siyah çizgi/ok, beyaz yazı, siyah arkaplan
    this.cizgiRengi = '#000000';
    this.okRengi = '#000000';
    this.metinRengi = '#ffffff';
    this.metinArkaplanRengi = '#000000';
    this.metinArkaplanSaydamlik = 100;
    this.cizgiKalinligi = 2;
    this.metinBoyutu = 14;
    this.okBoyutu = 8;
    this.okStili = 'ok';
    this.ondalikBasamak = 2;
    this.gorunur = true;
    this.cizgiGorunur = true;
    this.okGorunur = true;
    this.metinGorunur = true;
    this.uzatmaCizgisiGorunur = true;
    this.glowEfekti = false;
    this.golgeEfekti = true;

    // Tüm değişiklikleri uygula
    this.gorunurlukDegisti();
    this.cizgiRengiDegisti();
    this.okRengiDegisti();
    this.cizgiKalinligiDegisti();
    this.okBoyutuDegisti();
    this.cizgiGorunurlukDegisti();
    this.okGorunurlukDegisti();
    this.metinGorunurlukDegisti();
    this.uzatmaCizgisiGorunurlukDegisti();
    this.olcumDegeriDegisti();

    this.CD.detectChanges();
  }

  kaydet(): void {
    this.guncellendi.emit();
    this.dialogKapat();
  }

  iptal(): void {
    // Orijinal değerlere geri dön
    Object.assign(this, this.orijinalDegerler);

    // Değişiklikleri uygula
    this.gorunurlukDegisti();
    this.cizgiRengiDegisti();
    this.okRengiDegisti();
    this.cizgiKalinligiDegisti();
    this.okBoyutuDegisti();
    this.cizgiGorunurlukDegisti();
    this.okGorunurlukDegisti();
    this.metinGorunurlukDegisti();
    this.uzatmaCizgisiGorunurlukDegisti();
    this.olcumDegeriDegisti();

    this.dialogKapat();
  }

  private dialogKapat(): void {
    this.kapandi.emit();

    if (this.pencere?.instance?.kapa) {
      this.pencere.instance.kapa();
    } else if (this.pencere?.kapa) {
      this.pencere.kapa();
    }
  }

  // ==================== YARDIMCI ====================

  private sahneYenile(): void {
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  private tarihFormatla(tarih: Date | undefined): string {
    if (!tarih) return '-';
    const d = new Date(tarih);
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }

  koordinatFormatla(deger: number): string {
    return (deger ?? 0).toFixed(3);
  }

  // ==================== v3.0.0: PLAIN GROUP GEOMETRY REBUILD ====================

  /**
   * 🎯 v3.0.4: Çizgi tabanlı obje kontrolü
   * THREE.Line, LineSegments2, Line2, ve Mesh+LineGeometry kombinasyonunu destekler
   */
  private isLineBased(obj: THREE.Object3D): boolean {
    // instanceof ile kontrol
    if (obj instanceof THREE.Line) return true;

    // Constructor name ile kontrol (LineSegments2, Line2, LineLoop vb.)
    const name = obj.constructor.name;
    if (name.includes('Line') || name.includes('Segments')) return true;

    // type alanı ile kontrol
    const type = (obj as any).type || '';
    if (type.includes('Line')) return true;

    // 🎯 v3.0.4: Mesh + LineGeometry kombinasyonu kontrolü
    // Debug'da görüldü: Mesh | geo: LineGeometry | hasSetPositions: true
    if (obj instanceof THREE.Mesh) {
      const geo = (obj as any).geometry;
      if (geo) {
        const geoType = geo.type || geo.constructor?.name || '';
        const hasSetPos = typeof geo.setPositions === 'function';
        // LineGeometry, LineSegmentsGeometry vb. içeriyorsa bu bir çizgidir
        if (geoType.includes('Line') && hasSetPos) {
          return true;
        }
        // 🎯 v3.0.4 FALLBACK: geoType Line içermese bile hasSetPositions varsa çizgi olabilir
        // (Bazı Line2/LineSegments2 implementasyonlarında geometry.type farklı olabilir)
        if (hasSetPos) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * 🎯 v3.0.5: Plain Group'tan orijinal pointA ve pointB'yi çıkar
   * Bu noktalar SABİT KALACAK - sadece dimension görüntüsü değişecek
   *
   * KRITIK: Extension line'ların BAŞLANGIÇ noktaları gerçek ölçüm noktalarıdır
   * Extension line yapısı: startPoint -> endPoint (endPoint dimension line üzerinde)
   * Bu yüzden her extension line'ın başlangıç noktasını almalıyız
   */
  private extractPlainGroupPoints(): void {
    // Önce userData'dan dene
    const userData = this.olcumGrubu.userData || {};

    if (userData.startPoint && userData.endPoint) {
      this.plainGroupPointA.set(
        userData.startPoint.x ?? 0,
        userData.startPoint.y ?? 0,
        userData.startPoint.z ?? 0
      );
      this.plainGroupPointB.set(
        userData.endPoint.x ?? 0,
        userData.endPoint.y ?? 0,
        userData.endPoint.z ?? 0
      );
      // console.log('[OlcumDuzenleDialog] v3.0.5 pointA/B userData\'dan alındı:', this.plainGroupPointA.toArray(), this.plainGroupPointB.toArray());
      return;
    }

    // point1/point2 dene
    if (userData.point1 && userData.point2) {
      this.plainGroupPointA.set(userData.point1.x ?? 0, userData.point1.y ?? 0, userData.point1.z ?? 0);
      this.plainGroupPointB.set(userData.point2.x ?? 0, userData.point2.y ?? 0, userData.point2.z ?? 0);
      // console.log('[OlcumDuzenleDialog] v3.0.5 pointA/B point1/2\'den alındı');
      return;
    }

    // 🎯 v3.0.5: ölçüm değerinden mesafeyi al
    const olcumMesafesi = this.olcumDegeri; // mm cinsinden
    // console.log('[OlcumDuzenleDialog] v3.0.5 Ölçüm mesafesi:', olcumMesafesi, this.birim);

    // Children'dan çıkar - Tüm çizgi tiplerinden uç noktaları al
    const allPoints: THREE.Vector3[] = [];
    this.olcumGrubu.updateMatrixWorld(true);

    this.olcumGrubu.traverse((child) => {
      // 🎯 v3.0.4: isLineBased ile tüm çizgi tiplerini yakala
      if (this.isLineBased(child) && allPoints.length < 50) {
        const geo = (child as any).geometry;
        if (!geo) return;

        child.updateMatrixWorld(true);
        const worldMatrix = child.matrixWorld;

        let pointsFound = false;

        // 🎯 v3.0.4: LineGeometry için instanceStart/instanceEnd dene
        if (typeof geo.getAttribute === 'function') {
          const instanceStart = geo.getAttribute('instanceStart');
          const instanceEnd = geo.getAttribute('instanceEnd');

          if (instanceStart && instanceEnd && instanceStart.count > 0) {
            for (let i = 0; i < instanceStart.count && allPoints.length < 50; i++) {
              const ps = new THREE.Vector3(instanceStart.getX(i), instanceStart.getY(i), instanceStart.getZ(i));
              const pe = new THREE.Vector3(instanceEnd.getX(i), instanceEnd.getY(i), instanceEnd.getZ(i));
              ps.applyMatrix4(worldMatrix);
              pe.applyMatrix4(worldMatrix);
              allPoints.push(ps, pe);
            }
            pointsFound = true;
            // console.log('[OlcumDuzenleDialog] v3.0.4 instanceStart/End\'den nokta alındı:', instanceStart.count * 2);
          }
        }

        // 🎯 v3.0.4: position attribute dene (instanceStart yoksa veya boşsa)
        if (!pointsFound && typeof geo.getAttribute === 'function') {
          const posAttr = geo.getAttribute('position');
          if (posAttr && posAttr.count >= 2) {
            for (let i = 0; i < posAttr.count && allPoints.length < 50; i++) {
              const p = new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
              p.applyMatrix4(worldMatrix);
              allPoints.push(p);
            }
            pointsFound = true;
            // console.log('[OlcumDuzenleDialog] v3.0.4 position attribute\'dan nokta alındı:', posAttr.count);
          }
        }

        // 🎯 v3.0.4: Fallback - attributes objesi üzerinden dene
        if (!pointsFound && geo.attributes) {
          const attrs = geo.attributes;
          // instanceStart varsa
          if (attrs.instanceStart && attrs.instanceEnd) {
            const is = attrs.instanceStart;
            const ie = attrs.instanceEnd;
            for (let i = 0; i < is.count && allPoints.length < 50; i++) {
              const ps = new THREE.Vector3(is.getX(i), is.getY(i), is.getZ(i));
              const pe = new THREE.Vector3(ie.getX(i), ie.getY(i), ie.getZ(i));
              ps.applyMatrix4(worldMatrix);
              pe.applyMatrix4(worldMatrix);
              allPoints.push(ps, pe);
            }
            pointsFound = true;
            // console.log('[OlcumDuzenleDialog] v3.0.4 attributes.instanceStart/End\'den nokta alındı');
          }
          // position varsa
          else if (attrs.position) {
            const pos = attrs.position;
            for (let i = 0; i < pos.count && allPoints.length < 50; i++) {
              const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
              p.applyMatrix4(worldMatrix);
              allPoints.push(p);
            }
            pointsFound = true;
            // console.log('[OlcumDuzenleDialog] v3.0.4 attributes.position\'dan nokta alındı');
          }
        }

        // 🎯 v3.0.4: Son çare - Line2/Mesh için child pozisyonunu al
        if (!pointsFound) {
          // Mesh pozisyonu bile bir ipucu olabilir
          const meshPos = child.position.clone();
          meshPos.applyMatrix4(child.parent?.matrixWorld || new THREE.Matrix4());
          allPoints.push(meshPos);
          console.warn('[OlcumDuzenleDialog] v3.0.4 Fallback mesh pozisyon alındı:', child.constructor.name, meshPos.toArray());
        }
      }
    });

    // 🎯 v3.0.4: Eğer hala nokta yoksa, Sprite pozisyonlarından tahmin et
    if (allPoints.length < 2) {
      console.warn('[OlcumDuzenleDialog] v3.0.4 Çizgilerden yeterli nokta alınamadı, Sprite kullanılıyor');
      this.olcumGrubu.traverse((child) => {
        if (child instanceof THREE.Sprite) {
          const spriteWorldPos = new THREE.Vector3();
          child.getWorldPosition(spriteWorldPos);
          // Label'ın dünya pozisyonu dimension line ortasını gösterir
          // Extension line uçlarını tahmin etmek için label pozisyonunu kullan
          // console.log('[OlcumDuzenleDialog] v3.0.4 Sprite world pozisyonu:', spriteWorldPos.toArray());
        }
      });
    }

    // console.log('[OlcumDuzenleDialog] v3.0.2 geometriden bulunan nokta sayısı:', allPoints.length);

    if (allPoints.length >= 2) {
      // En uzak iki noktayı bul (bunlar muhtemelen extension line uçları)
      let maxDist = 0;
      let bestP1 = allPoints[0];
      let bestP2 = allPoints[1];

      for (let i = 0; i < allPoints.length; i++) {
        for (let j = i + 1; j < allPoints.length; j++) {
          const dist = allPoints[i].distanceTo(allPoints[j]);
          if (dist > maxDist) {
            maxDist = dist;
            bestP1 = allPoints[i];
            bestP2 = allPoints[j];
          }
        }
      }

      this.plainGroupPointA.copy(bestP1);
      this.plainGroupPointB.copy(bestP2);

      // userData'ya kaydet (sonraki kullanımlar için)
      this.olcumGrubu.userData.startPoint = bestP1.clone();
      this.olcumGrubu.userData.endPoint = bestP2.clone();

      // console.log('[OlcumDuzenleDialog] v3.0.2 pointA/B geometriden hesaplandı:', this.plainGroupPointA.toArray(), this.plainGroupPointB.toArray(), 'maxDist:', maxDist);
    } else {
      // Fallback: Component pozisyonları kullan
      this.plainGroupPointA.set(this.pozisyon1.x, this.pozisyon1.y, this.pozisyon1.z);
      this.plainGroupPointB.set(this.pozisyon2.x, this.pozisyon2.y, this.pozisyon2.z);
      // console.log('[OlcumDuzenleDialog] v3.0.2 pointA/B fallback pozisyonlardan alındı');
    }
  }

  /**
   * 🎯 v3.0.2: Mevcut offset'i hesapla
   * Dimension line ile orijinal noktalar arası mesafe
   * Daha güvenilir: Label'ın LOCAL pozisyonunu kullan
   *
   * 🎯 v3.2.6 FIX: Gerçek offset YÖNÜNÜ de hesapla ve dragState.offsetDirection'a kaydet
   * Bu sayede drag başladığında yön tutarsızlığı olmaz
   */
  private calculateCurrentOffset(): void {
    // Sprite (label) pozisyonundan offset hesapla
    let labelPos: THREE.Vector3 | null = null;
    let foundSprite: THREE.Sprite | null = null;

    this.olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite && !foundSprite) {
        foundSprite = child;
      }
    });

    if (foundSprite) {
      // 🎯 v3.0.2: LOCAL pozisyon kullan (world değil) - daha güvenilir
      labelPos = foundSprite.position.clone();

      // Label ile pointA-pointB çizgisi arasındaki mesafe = offset
      const lineDir = new THREE.Vector3().subVectors(this.plainGroupPointB, this.plainGroupPointA).normalize();
      const lineCenter = new THREE.Vector3().lerpVectors(this.plainGroupPointA, this.plainGroupPointB, 0.5);
      const toLabel = new THREE.Vector3().subVectors(labelPos, lineCenter);

      // Çizgiye dik bileşen = offset
      const alongLine = lineDir.clone().multiplyScalar(toLabel.dot(lineDir));
      const perpendicular = toLabel.clone().sub(alongLine);
      const calculatedOffset = perpendicular.length();

      // 🎯 v3.2.6 FIX: GERÇEK offset yönünü hesapla (normalize edilmiş perpendicular)
      // Bu yön, ölçümün mevcut konumuna göre belirlenir
      if (calculatedOffset > 0.001) {
        // Gerçek yön: label'ın lineCenter'dan uzaklaştığı yön
        this.dragState.offsetDirection.copy(perpendicular).normalize();
        // console.log('[OlcumDuzenleDialog] v3.2.6 Gerçek offset yönü hesaplandı:',
        //   this.dragState.offsetDirection.toArray().map(v => v.toFixed(3)));
      }

      // 🎯 v3.0.2: Makul bir değer mi kontrol et (0-200 arası)
      if (calculatedOffset > 0 && calculatedOffset < 200) {
        this.plainGroupOffset = calculatedOffset;
        // console.log('[OlcumDuzenleDialog] v3.2.6 Offset hesaplandı:', this.plainGroupOffset.toFixed(2));
      } else {
        // Çok büyük veya sıfır - dimension line'dan hesapla
        this.calculateOffsetFromDimensionLine();
      }
    } else {
      // Sprite yok - dimension line'dan hesapla
      this.calculateOffsetFromDimensionLine();
    }
  }

  /**
   * 🎯 v3.0.2: Dimension line'dan offset hesapla (fallback)
   * Ana dimension çizgisinin orta noktasını kullan
   */
  private calculateOffsetFromDimensionLine(): void {
    // İlk çizginin (main dimension line) orta noktasını bul
    let dimensionLineCenter: THREE.Vector3 | null = null;

    this.olcumGrubu.traverse((child) => {
      if (!dimensionLineCenter && this.isLineBased(child)) {
        const geo = (child as any).geometry;
        if (!geo) return;

        // setPositions varsa LineGeometry
        if (typeof geo.setPositions === 'function') {
          const instanceStart = geo.getAttribute?.('instanceStart');
          const instanceEnd = geo.getAttribute?.('instanceEnd');
          if (instanceStart && instanceEnd && instanceStart.count > 0) {
            const p1 = new THREE.Vector3(instanceStart.getX(0), instanceStart.getY(0), instanceStart.getZ(0));
            const p2 = new THREE.Vector3(instanceEnd.getX(0), instanceEnd.getY(0), instanceEnd.getZ(0));
            dimensionLineCenter = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
          }
        } else {
          const posAttr = geo.getAttribute?.('position');
          if (posAttr && posAttr.count >= 2) {
            const p1 = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
            const p2 = new THREE.Vector3(posAttr.getX(1), posAttr.getY(1), posAttr.getZ(1));
            dimensionLineCenter = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
          }
        }
      }
    });

    if (dimensionLineCenter) {
      // pointA-pointB çizgisinin ortasına olan mesafe = offset
      const lineCenter = new THREE.Vector3().lerpVectors(this.plainGroupPointA, this.plainGroupPointB, 0.5);
      const offset = dimensionLineCenter.distanceTo(lineCenter);

      // Makul bir değer mi?
      if (offset > 0 && offset < 200) {
        this.plainGroupOffset = offset;
        // console.log('[OlcumDuzenleDialog] v3.0.2 Offset dimension line\'dan hesaplandı:', this.plainGroupOffset.toFixed(2));
        return;
      }
    }

    // Son fallback: varsayılan değer
    this.plainGroupOffset = 15;
    // console.log('[OlcumDuzenleDialog] v3.0.2 Offset fallback: 15');
  }

  /**
   * 🎯 v3.0.2: Plain Group geometrisini yeniden oluştur
   * LinearDimension.buildGeometry() ile aynı mantık
   * pointA ve pointB SABİT KALIR, sadece dimension görüntüsü değişir
   *
   * isLineBased() ile tüm çizgi tiplerini destekler (THREE.Line, LineSegments2, Line2 vb.)
   * 🎯 v3.2.5: Drag sırasında tutarlı yön için dragState.offsetDirection kullan
   * 🎯 v3.3.4: Koordinat için FikoOlcumCizgisi kendi sistemini kullanır
   */
  private rebuildPlainGroupGeometry(): void {
    if (!this.olcumGrubu) return;

    // 🎯 v3.3.4: FikoOlcumCizgisi için rebuild yapmayı atla
    // FikoOlcumCizgisi kendi sürükleme sistemini kullanır (suruklemeyiBaslat)
    if (this.olcumGrubu instanceof FikoOlcumCizgisi) {
      // console.log('[OlcumDuzenleDialog] v3.3.4 FikoOlcumCizgisi için plain group rebuild atlandı');
      return;
    }

    const offset = this.plainGroupOffset;
    const rotation = this.plainGroupRotation;

    // Yön vektörü (pointA -> pointB)
    const direction = new THREE.Vector3()
      .subVectors(this.plainGroupPointB, this.plainGroupPointA)
      .normalize();

    // 🎯 v3.2.5: Drag aktifse dragState.offsetDirection kullan (tutarlılık için)
    // Aksi halde yeniden hesapla
    let offsetDir: THREE.Vector3;
    if (this.dragState.active && this.dragState.offsetDirection.lengthSq() > 0.0001) {
      // Drag sırasında: önceden hesaplanmış BASE yönü kullan
      // NOT: dragState.offsetDirection rotation'suz BASE direction
      offsetDir = this.dragState.offsetDirection.clone();

      // 🎯 v3.2.5 FIX: Mevcut TOPLAM rotation'ı uygula, delta DEĞİL!
      // dragState.offsetDirection rotation'suz base, biz üzerine FULL rotation ekliyoruz
      // Çünkü offset hesabı için final direction gerekli
      if (Math.abs(rotation) > 0.0001) {
        const quaternion = new THREE.Quaternion();
        quaternion.setFromAxisAngle(direction, rotation);
        offsetDir.applyQuaternion(quaternion);
      }

      // console.log('[OlcumDuzenleDialog] v3.2.5 Drag aktif - baseOffsetDir:', this.dragState.offsetDirection.toArray().map(v => v.toFixed(3)),
      //             'fullRotation:', rotation.toFixed(3), 'finalOffsetDir:', offsetDir.toArray().map(v => v.toFixed(3)));
    } else {
      // Normal mod: yeniden hesapla
      offsetDir = this.calculateRotatedOffsetDirection(direction, rotation);
    }

    // Offset noktaları (dimension line üzerindeki noktalar)
    const offsetA = this.plainGroupPointA.clone().addScaledVector(offsetDir, offset);
    const offsetB = this.plainGroupPointB.clone().addScaledVector(offsetDir, offset);

    // Label pozisyonu (dimension line ortası + biraz yukarı)
    const labelPos = new THREE.Vector3().lerpVectors(offsetA, offsetB, 0.5);
    labelPos.addScaledVector(offsetDir, 2); // textOffset

    // Extension line hesaplamaları
    const gap = 0.5;
    const overshoot = 3;

    const extDir1 = new THREE.Vector3().subVectors(offsetA, this.plainGroupPointA).normalize();
    const ext1Start = this.plainGroupPointA.clone().addScaledVector(extDir1, gap);
    const ext1End = offsetA.clone().addScaledVector(extDir1, overshoot);

    const extDir2 = new THREE.Vector3().subVectors(offsetB, this.plainGroupPointB).normalize();
    const ext2Start = this.plainGroupPointB.clone().addScaledVector(extDir2, gap);
    const ext2End = offsetB.clone().addScaledVector(extDir2, overshoot);

    // Children'ları güncelle - collect all items first (traverse sıralaması garantili değil)
    const lines: THREE.Object3D[] = [];
    const arrows: THREE.Mesh[] = [];
    let sprite: THREE.Sprite | null = null;

    this.olcumGrubu.traverse((child) => {
      // 🎯 v3.0.2: isLineBased() ile tüm çizgi tiplerini yakala
      if (this.isLineBased(child)) {
        lines.push(child);
      }
      // Oklar (Mesh ama Sprite değil) - Group değilse ve Line değilse
      else if (child instanceof THREE.Mesh && !(child instanceof THREE.Sprite) && !this.isLineBased(child)) {
        arrows.push(child);
      }
      // Label (Sprite)
      else if (child instanceof THREE.Sprite) {
        sprite = child;
      }
    });

    // console.log('[OlcumDuzenleDialog] v3.0.4 Bulunan objeler - çizgi:', lines.length, 'ok:', arrows.length, 'sprite:', !!sprite);

    // Çizgileri güncelle
    lines.forEach((line, lineIndex) => {
      const geo = (line as any).geometry;
      if (!geo) return;

      // LineGeometry için setPositions() kullan
      if (typeof geo.setPositions === 'function') {
        if (lineIndex === 0) {
          // Main dimension line: offsetA -> offsetB
          geo.setPositions([offsetA.x, offsetA.y, offsetA.z, offsetB.x, offsetB.y, offsetB.z]);
        } else if (lineIndex === 1) {
          // Extension line 1
          geo.setPositions([ext1Start.x, ext1Start.y, ext1Start.z, ext1End.x, ext1End.y, ext1End.z]);
        } else if (lineIndex === 2) {
          // Extension line 2
          geo.setPositions([ext2Start.x, ext2Start.y, ext2Start.z, ext2End.x, ext2End.y, ext2End.z]);
        }

        // computeLineDistances varsa çağır
        if (typeof (line as any).computeLineDistances === 'function') {
          (line as any).computeLineDistances();
        }
      }
      // Normal BufferGeometry için position attribute kullan
      else {
        const posAttr = geo.getAttribute?.('position');
        if (posAttr && posAttr.count >= 2) {
          if (lineIndex === 0) {
            posAttr.setXYZ(0, offsetA.x, offsetA.y, offsetA.z);
            posAttr.setXYZ(1, offsetB.x, offsetB.y, offsetB.z);
          } else if (lineIndex === 1) {
            posAttr.setXYZ(0, ext1Start.x, ext1Start.y, ext1Start.z);
            posAttr.setXYZ(1, ext1End.x, ext1End.y, ext1End.z);
          } else if (lineIndex === 2) {
            posAttr.setXYZ(0, ext2Start.x, ext2Start.y, ext2Start.z);
            posAttr.setXYZ(1, ext2End.x, ext2End.y, ext2End.z);
          }

          posAttr.needsUpdate = true;
          geo.computeBoundingBox?.();
          geo.computeBoundingSphere?.();
        }
      }
    });

    // Okları güncelle
    arrows.forEach((arrow, arrowIndex) => {
      if (arrowIndex === 0) {
        // Arrow 1: offsetA'da, direction yönünde
        arrow.position.copy(offsetA);
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);
      } else if (arrowIndex === 1) {
        // Arrow 2: offsetB'de, -direction yönünde
        arrow.position.copy(offsetB);
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().negate());
        arrow.quaternion.copy(quaternion);
      }
    });

    // Label'ı güncelle
    if (sprite) {
      sprite.position.copy(labelPos);
    }

    // console.log('[OlcumDuzenleDialog] v3.0.2 Geometry rebuild tamamlandı - lines:', lines.length, 'arrows:', arrows.length, 'offset:', offset.toFixed(2), 'rotation:', rotation.toFixed(2));
  }

  /**
   * 🎯 v3.0.0: Döndürülmüş offset yönünü hesapla
   * LinearDimension.calculateOffsetDirection() ile aynı mantık
   */
  private calculateRotatedOffsetDirection(direction: THREE.Vector3, rotationAngle: number): THREE.Vector3 {
    // Varsayılan yukarı yön
    const up = new THREE.Vector3(0, 1, 0);

    // Çizgiye dik yön hesapla
    const offsetDir = new THREE.Vector3().crossVectors(direction, up);

    // Eğer paralelse (dikey çizgi), X yönünü kullan
    if (offsetDir.lengthSq() < 0.0001) {
      offsetDir.crossVectors(direction, new THREE.Vector3(1, 0, 0));
    }

    offsetDir.normalize();

    // Her zaman yukarı doğru olsun (tutarlılık için)
    if (offsetDir.y < 0) {
      offsetDir.negate();
    }

    // Döndürme açısını uygula (ölçüm ekseni etrafında)
    if (rotationAngle !== 0) {
      const quaternion = new THREE.Quaternion();
      quaternion.setFromAxisAngle(direction, rotationAngle);
      offsetDir.applyQuaternion(quaternion);
    }

    return offsetDir;
  }

  /**
   * Başlık stringinden değer ve birimi parse et
   * 🎯 v3.2.7: Çap (Ø, ⌀) ve yarıçap (R) prefix'lerini destekle
   * Örnekler:
   *   "94.98 mm" -> { deger: 94.98, birim: "mm" }
   *   "Ø 15.99 mm" -> { deger: 15.99, birim: "mm" }
   *   "⌀ 15.99 mm" -> { deger: 15.99, birim: "mm" }
   *   "R 7.5 mm" -> { deger: 7.5, birim: "mm" }
   */
  private baslikDegerParseEt(baslik: string): { deger: number, birim: string } | null {
    if (!baslik || typeof baslik !== 'string') return null;

    // 🎯 v3.2.7: Önce prefix'leri kaldır (Ø, ⌀, R, φ, Φ)
    let temizBaslik = baslik.trim();
    temizBaslik = temizBaslik.replace(/^[ØøΦφ⌀R]\s*/i, '');

    // Regex: sayı (ondalıklı olabilir) + opsiyonel birim
    const match = temizBaslik.match(/^[\s]*([\d.,]+)[\s]*(mm|cm|m|inch|ft|in)?[\s]*$/i);
    if (match) {
      // Virgülü noktaya çevir ve parse et
      const degerStr = match[1].replace(',', '.');
      const deger = parseFloat(degerStr);
      if (!isNaN(deger)) {
        return {
          deger: deger,
          birim: match[2]?.toLowerCase() || 'mm'
        };
      }
    }
    return null;
  }

  // Template'den erişim için
  Math = Math;

  // ====================================================================
  // 🎯 v3.2.0: OFFSET TABANLI SÜRÜKLEME SİSTEMİ
  // pointA ve pointB SABİT kalır, sadece offset değişir
  // ====================================================================

  /**
   * 🎯 v3.2.0: Pointer down - Sürükleme başlat (offset tabanlı)
   */
  private onPointerDownSimple(event: PointerEvent): void {
    // Sol tık kontrolü
    if (event.button !== 0) return;

    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera || !this.canvasElement) return;

    // Mouse pozisyonunu normalize et
    const rect = this.canvasElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    // Raycaster ile ölçüm grubuna tıklanıp tıklanmadığını kontrol et
    const raycaster = OlcumDuzenleDialogComponent._raycaster;
    raycaster.setFromCamera(mouse, kamera);

    const intersects = raycaster.intersectObject(this.olcumGrubu, true);
    if (intersects.length === 0) return;

    // Sürükleme başlat
    this.dragState.active = true;
    this.dragState.startMouse.copy(mouse);
    this.dragState.startDimensionOffset = this.plainGroupOffset;

    // 🎯 v3.2.0: Mevcut offset yönünü kullan (geometry ile tutarlı olmalı)
    const lineDir = new THREE.Vector3()
      .subVectors(this.plainGroupPointB, this.plainGroupPointA)
      .normalize();

    // calculateRotatedOffsetDirection ile aynı yön - TUTARLILIK İÇİN
    this.dragState.offsetDirection.copy(
      this.calculateRotatedOffsetDirection(lineDir, this.plainGroupRotation)
    );

    // Ölçüm merkezi
    const center = new THREE.Vector3()
      .lerpVectors(this.plainGroupPointA, this.plainGroupPointB, 0.5);

    // Kamera yönü
    const cameraDir = new THREE.Vector3();
    kamera.getWorldDirection(cameraDir);

    // Sürükleme düzlemi (kamera yönüne dik)
    this.dragPlane.setFromNormalAndCoplanarPoint(cameraDir, center);

    // İlk intersection noktasını kaydet
    raycaster.ray.intersectPlane(this.dragPlane, this.dragIntersection);

    // Cursor değiştir
    this.canvasElement.style.cursor = 'grabbing';

    // Event'i durdur
    event.preventDefault();
    event.stopPropagation();

    // console.log('[OlcumDuzenleDialog] v3.2.0 Sürükleme başladı - offset:', this.plainGroupOffset,
    //   'offsetDir:', this.dragState.offsetDirection.toArray());
  }

  /**
   * 🎯 v3.2.0: Pointer move - Offset güncelle (pointA/B sabit kalır!)
   */
  private onPointerMoveSimple(event: PointerEvent): void {
    if (!this.dragState.active) return;

    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera || !this.canvasElement) return;

    // Mouse pozisyonunu normalize et
    const rect = this.canvasElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    // Raycaster
    const raycaster = OlcumDuzenleDialogComponent._raycaster;
    raycaster.setFromCamera(mouse, kamera);

    // Düzlemle kesişim noktasını bul
    const newIntersection = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(this.dragPlane, newIntersection)) return;

    // 🎯 v3.2.0: Hareket farkını offset yönüne projekte et
    const delta = new THREE.Vector3().subVectors(newIntersection, this.dragIntersection);
    const offsetDelta = delta.dot(this.dragState.offsetDirection);

    // 🎯 v3.2.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
    const newOffset = this.dragState.startDimensionOffset + offsetDelta;
    this.plainGroupOffset = newOffset;

    // 🎯 v3.2.0: Geometry'yi yeniden oluştur (pointA/B SABİT!)
    this.rebuildPlainGroupGeometry();

    // Sahneyi yenile
    this.anaGosterim?.ciz?.();

    // Event'i durdur
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * 🎯 v3.2.0: Pointer up - Sürükleme bitir
   */
  private onPointerUpSimple(event: PointerEvent): void {
    if (!this.dragState.active) return;

    this.dragState.active = false;

    // Cursor'u geri al
    if (this.canvasElement) {
      this.canvasElement.style.cursor = 'grab';
    }

    // Sahneyi yenile
    this.anaGosterim?.ciz?.();

    // console.log('[OlcumDuzenleDialog] v3.2.0 Sürükleme bitti - yeni offset:', this.plainGroupOffset);
  }
}
